import PropTypes from "prop-types";
import { useState, useEffect, useRef } from "react";
import "../styles.css";
import { rgbaToHex } from "../../../../util/canvas/canvasColorUtil";
import SimpleLexicalEditorWrapper from "../text-editor/SimpleLexicalEditor";
import generateHtmlWithStylesFromNodes from "../canvas-core/generateHtmlWithStylesFromNodes";
import CopyIcon from "../selection/CopyIcon";
import {
  getCanvasStageHeightPx,
  getMainCanvasMaxRadiusPx,
  getOffCanvasStageWidthPx,
  getMainCanvasWidthPx,
  getStageHeightPxFromCanvasHeight,
} from "../../utils/mainCanvasLayout";

const TYPING_INPUT_TYPES = new Set([
  "insertText",
  "deleteContentBackward",
  "deleteContentForward",
  "insertCompositionText",
  "insertFromPaste",
  "insertFromDrop",
  "deleteByCut",
]);

const shouldDeferInputUpdate = (inputType) =>
  inputType && TYPING_INPUT_TYPES.has(inputType);

const resolveCanvasHeightPx = (canvasHeight) =>
  Number(canvasHeight) > 100
    ? Number(canvasHeight)
    : getStageHeightPxFromCanvasHeight(canvasHeight);

const clampNumericValue = (value, min, max) => {
  console.log("DEBUG: clampNumericValue called:", {
    value: value,
    min: min,
    max: max
  });
  if (value === "" || value === null || value === undefined) {
    console.log("DEBUG: clampNumericValue returning null (empty)");
    return null;
  }
  let num = parseInt(String(value), 10);
  if (Number.isNaN(num)) {
    console.log("DEBUG: clampNumericValue returning null (NaN)");
    return null;
  }
  if (typeof min === "number" && num < min) {
    console.log("DEBUG: clampNumericValue clamping to min:", min);
    num = min;
  }
  if (typeof max === "number" && num > max) {
    console.log("DEBUG: clampNumericValue clamping to max:", max);
    num = max;
  }
  console.log("DEBUG: clampNumericValue returning:", num);
  return num;
};

// Helper function to convert negative x/y values to 0 for display only
const getDisplayValue = (value) => {
  if (value === "" || value === null || value === undefined) return value;
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return num < 0 ? "0" : value;
};

const createDefaultCirclePreset = () => ({
  id: "circle-default-preset",
  name: "Default Circle",
  fill: "#FFFFFF",
  fillOpacity: 1,
  stroke: "#FF0000",
  strokeOpacity: 1,
  strokeWidth: 2,
  fontSize: 15,
  textColor: "#000000",
  fontStyle: "normal",
  textDecoration: "none",
  align: "center",
  verticalAlign: "middle",
  text: "",
  rotation: 0,
  radius: 25,
  radiusX: 25,
  radiusY: 25,
  maintainAspectRatio: true,
});

function CircleEditor({
  selectedCircle,
  updateCircleProperties,
  onAddText,
  onEditText,
  onDelete,
  onRemoveText,
  canvasHeight,
  mainCanvasWidthPx,
  onToggleCloneMode = null,
  isCloneMode = false,
}) {
  const DEFAULT_PRESET_STORAGE_KEY = "circleDefaultStylePreset";
  const DEFAULT_PRESET_ID_STORAGE_KEY = "circleDefaultStylePresetId";
  const EDITOR_EDGE_BUFFER_PX = 10;
  const MIN_RADIUS = 5;
  const MIN_X = 0;
  const MIN_Y = 0;

  const MIN_ROTATION = -360;
  const MAX_ROTATION = 360;
  const MIN_STROKE_WIDTH = 1;
  const MAX_STROKE_WIDTH = 20;
  const MIN_FONT_SIZE = 5;
  const MAX_FONT_SIZE = 200; // Fixed maximum font size instead of radius-dependent

  const MAX_HEIGHT = 600;

  const mainStageW = Math.max(
    MIN_RADIUS,
    Number(mainCanvasWidthPx) || getMainCanvasWidthPx(),
  );
  const mainStageH = resolveCanvasHeightPx(canvasHeight);
  // Use same MAX_RADIUS as CircleElement for consistency (hardcoded 350 limit)
  const computedMaxRadius = getMainCanvasMaxRadiusPx(
    Number.isFinite(Number(mainCanvasWidthPx)) && Number(mainCanvasWidthPx) > 0
      ? Number(mainCanvasWidthPx) / 0.45
      : undefined,
    mainStageH,
  );
  const MAX_RADIUS = Math.min(computedMaxRadius, 350);

  // For oval support - using radiusX and radiusY instead of width/height
  const MIN_RADIUS_X = 5;
  const MAX_RADIUS_X = MAX_RADIUS;
  const MIN_RADIUS_Y = 5;
  const MAX_RADIUS_Y = MAX_RADIUS;

  // Determine if circle is on off-canvas by checking x position
  // Note: Negative x values are allowed for main canvas stage elements
  // For off-canvas circles, we need to use off-canvas stage dimensions
  // CRITICAL FIX: Check _isInOffCanvasDuringDrag flag first (for elements being dragged between canvases)
  // If flag is explicitly false, element is on main canvas (don't check x position)
  // If flag is true, element is on off-canvas
  // If flag is undefined, fall back to x position check for elements already on off-canvas
  const isOffCanvas =
    selectedCircle?._previewTargetStage === "offCanvas"
      ? true
      : selectedCircle?._previewTargetStage === "main"
        ? false
        : (selectedCircle?._isDragging === true &&
            selectedCircle?.isOffCanvas === true) ||
          (selectedCircle?._isDragging === true &&
            selectedCircle?._previewTargetStage !== "main" &&
            (selectedCircle?.isOffCanvas === true ||
              selectedCircle?._isInOffCanvasDuringDrag === true ||
              (selectedCircle?._isInOffCanvasDuringDrag === undefined &&
                selectedCircle?.x !== undefined &&
                selectedCircle?.x >= mainStageW))) ||
          (selectedCircle?._isDragging !== true &&
            (selectedCircle?.isOffCanvas === true ||
              selectedCircle?._isInOffCanvasDuringDrag === true ||
              (selectedCircle?._isInOffCanvasDuringDrag === undefined &&
                selectedCircle?.x !== undefined &&
                selectedCircle?.x >= mainStageW)));

  // Calculate proper constraints based on canvas type
  const getStageConstraints = () => {
    const edgeInset = EDITOR_EDGE_BUFFER_PX;
    const strokeHalf = Math.max(
      0,
      Number(selectedCircle?.strokeWidth || 0) / 2,
    );
    if (isOffCanvas) {
      // Off-canvas stage uses full viewport width
      const stageWidth = getOffCanvasStageWidthPx();
      const stageHeight = getCanvasStageHeightPx();
      // For circles, x and y are center coordinates
      const radius =
        selectedCircle?.radius ||
        Math.max(selectedCircle?.radiusX || 0, selectedCircle?.radiusY || 0) ||
        0;
      return {
        minX: radius + strokeHalf + edgeInset,
        maxX: Math.max(
          radius + strokeHalf + edgeInset,
          stageWidth - radius - strokeHalf - edgeInset,
        ),
        minY: radius + strokeHalf + edgeInset,
        maxY: Math.max(
          radius + strokeHalf + edgeInset,
          stageHeight - radius - strokeHalf - edgeInset,
        ),
      };
    } else {
      // Main canvas constraints - allow negative x values
      // For circles, x and y are center coordinates
      const radius =
        selectedCircle?.radius ||
        Math.max(selectedCircle?.radiusX || 0, selectedCircle?.radiusY || 0) ||
        0;
      const canvasPixelWidth = mainStageW;
      const canvasPixelHeight = mainStageH;
      return {
        minX: -10000, // Allow negative x values for main canvas stage elements (center coordinate)
        maxX: Math.max(
          radius + strokeHalf + edgeInset,
          canvasPixelWidth - radius - strokeHalf - edgeInset,
        ),
        minY: radius + strokeHalf + edgeInset,
        maxY: Math.max(
          radius + strokeHalf + edgeInset,
          canvasPixelHeight - radius - strokeHalf - edgeInset,
        ),
      };
    }
  };

  const stageConstraints = getStageConstraints();
  const MIN_X_CONSTRAINED = stageConstraints.minX;
  const MAX_X_CONSTRAINED = stageConstraints.maxX;
  const MIN_Y_CONSTRAINED = stageConstraints.minY;
  const MAX_Y_CONSTRAINED = stageConstraints.maxY;
  const activeStageWidth = isOffCanvas ? getOffCanvasStageWidthPx() : mainStageW;
  const activeStageHeight = isOffCanvas
    ? getCanvasStageHeightPx()
    : resolveCanvasHeightPx(canvasHeight);
  const strokeHalf = Math.max(0, Number(selectedCircle?.strokeWidth || 0) / 2);
  // Radius input should clamp to the drawable off-canvas *side strip* width,
  // not the full combined stage width (main + off-canvas), so behavior matches
  // main-canvas circles (e.g. ~349 at default stroke/buffer).
  const effectiveRadiusCapStageWidth = isOffCanvas
    ? getMainCanvasWidthPx()
    : activeStageWidth;
  // Stage-fit maxima independent of current position.
  // Radius inputs should accept the largest shape that can exist on the stage,
  // then reposition x/y to keep the shape in bounds.
  // Use same MAX_RADIUS as CircleElement for consistency
  const maxRadiusByStageFit = Math.max(MIN_RADIUS, MAX_RADIUS);
  const maxRadiusXByStageFit = Math.max(MIN_RADIUS_X, MAX_RADIUS_X);
  const maxRadiusYByStageFit = Math.max(
    MIN_RADIUS_Y,
    // CRITICAL FIX: Use actual stage height constraint instead of being limited by MAX_RADIUS_Y
    // This allows radius Y to go up to the stage boundary (half stage height minus buffers)
    activeStageHeight / 2 - EDITOR_EDGE_BUFFER_PX - strokeHalf,
  );
  
  // DEBUG: Log calculated max values
  console.log("DEBUG: CircleEditor max values:", {
    effectiveRadiusCapStageWidth: effectiveRadiusCapStageWidth,
    strokeHalf: strokeHalf,
    EDITOR_EDGE_BUFFER_PX: EDITOR_EDGE_BUFFER_PX,
    activeStageHeight: activeStageHeight,
    maxRadiusByStageFit: maxRadiusByStageFit,
    maxRadiusXByStageFit: maxRadiusXByStageFit,
    maxRadiusYByStageFit: maxRadiusYByStageFit,
    MAX_RADIUS: MAX_RADIUS,
    MAX_RADIUS_X: MAX_RADIUS_X
  });

  // Then calculate safe max X and Y based on MAX_RADIUS (for backward compatibility)
  const MAX_X = getOffCanvasStageWidthPx() - MAX_RADIUS;
  const MAX_Y = 760;

  // Helper for number input: allow empty string, clamp values
  const handleNumberInputWithLimits = (field, value, min, max) => {
    let num = parseInt(value, 10);
    console.log("DEBUG: CircleEditor input change:", {
      field: field,
      value: value,
      num: num,
      min: min,
      max: max,
      isNaN: isNaN(num),
      isEmpty: value === ""
    });
    if (value === "" || isNaN(num)) {
      console.log("DEBUG: Setting to min due to empty/NaN:", min);
      updateCircleProperties({ [field]: min });
      return;
    }
    if (typeof min === "number" && num < min) {
      console.log("DEBUG: Clamping to min:", min);
      num = min;
    }
    if (typeof max === "number" && num > max) {
      console.log("DEBUG: Clamping to max:", max);
      num = max;
    }
    if (field === "y") {
    }
    console.log("DEBUG: CircleEditor final value:", {
      field: field,
      finalNum: num
    });
    updateCircleProperties({ [field]: num });
  };

  const handleEnterKey = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.target.blur();
    }
  };

  const [circlePresets, setCirclePresets] = useState(() => {
    const saved = localStorage.getItem("circleStylePresets");
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
    return [createDefaultCirclePreset()];
  });
  const [defaultPresetId, setDefaultPresetId] = useState(() =>
    localStorage.getItem(DEFAULT_PRESET_ID_STORAGE_KEY),
  );
  const [isStylesExpanded, setIsStylesExpanded] = useState(true);
  const [isStyleSectionExpanded, setIsStyleSectionExpanded] = useState(true);

  const [editingPreset, setEditingPreset] = useState(null);
  const [pendingDefaultPresetId, setPendingDefaultPresetId] = useState(null);
  const [defaultPresetIdBeforeEdit, setDefaultPresetIdBeforeEdit] =
    useState(null);
  const [pendingNewPresetId, setPendingNewPresetId] = useState(null);
  const [showTextEditor, setShowTextEditor] = useState(false);
  const [formattingUpdate, setFormattingUpdate] = useState(0); // State to trigger re-renders for formatting buttons

  // Local state for input values to avoid immediate updates
  const [localInputValues, setLocalInputValues] = useState({
    x:
      selectedCircle?.x !== undefined && selectedCircle?.x !== null
        ? isOffCanvas
          ? Math.max(stageConstraints.minX, Math.round(selectedCircle.x))
          : Math.round(selectedCircle.x)
        : "",
    y:
      selectedCircle?.y !== undefined && selectedCircle?.y !== null
        ? Math.max(0, Math.round(selectedCircle.y))
        : "",
    radius: selectedCircle?.radius ? Math.round(selectedCircle.radius) : "",
    radiusX: selectedCircle?.radiusX
      ? Math.round(selectedCircle.radiusX)
      : selectedCircle?.radius
        ? Math.round(selectedCircle.radius)
        : "",
    radiusY: selectedCircle?.radiusY
      ? Math.round(selectedCircle.radiusY)
      : selectedCircle?.radius
        ? Math.round(selectedCircle.radius)
        : "",
    rotation:
      selectedCircle?.rotation !== undefined
        ? Math.round(selectedCircle.rotation)
        : "",
    strokeWidth: selectedCircle?.strokeWidth
      ? Math.round(selectedCircle.strokeWidth)
      : "",
    fillOpacity:
      selectedCircle?.fillOpacity !== undefined
        ? Math.round(selectedCircle.fillOpacity * 100)
        : "",
    strokeOpacity:
      selectedCircle?.strokeOpacity !== undefined
        ? Math.round(selectedCircle.strokeOpacity * 100)
        : "",
    textColorHex: selectedCircle?.textColor
      ? selectedCircle.textColor.toUpperCase().replace("#", "")
      : "000000",
    strokeColorHex: selectedCircle?.stroke
      ? rgbaToHex(selectedCircle.stroke).toUpperCase().replace("#", "")
      : "000000",
  });

  // Aspect ratio state
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(
    selectedCircle?.maintainAspectRatio ?? true,
  );
  const [isEditingRotation, setIsEditingRotation] = useState(false);
  const [rotationInterval, setRotationInterval] = useState(null);
  const lastCircleIdRef = useRef(null);
  const isProgrammaticallyChangingFontSizeRef = useRef(false);
  const selectionUpdateListenersRef = useRef({ enabled: true });

  // Helper functions for rotation arrow holding
  const startRotationChange = (direction) => {
    const changeRotation = () => {
      setLocalInputValues((prev) => {
        const currentValue = prev.rotation;
        const currentNum = currentValue === "" ? 0 : parseInt(currentValue, 10);
        let newRotation = currentNum + direction;
        // Normalize to 0-360 range
        newRotation = newRotation % 360;
        if (newRotation < 0) {
          newRotation += 360;
        }
        return {
          ...prev,
          rotation: newRotation,
        };
      });
    };

    // Immediate change
    changeRotation();

    // Set up interval for continuous changes
    const interval = setInterval(changeRotation, 100); // 100ms delay
    setRotationInterval(interval);
  };

  const stopRotationChange = () => {
    if (rotationInterval) {
      clearInterval(rotationInterval);
      setRotationInterval(null);

      // CRITICAL FIX: Finalize rotation change when arrow button is released
      // Preserve x and y when rotation changes
      const val = localInputValues.rotation;
      if (val === "" || val === null || val === undefined) {
        applyRotationWithEllipseClamp(0);
      } else {
        const num = parseInt(val, 10);
        if (!isNaN(num)) {
          applyRotationWithEllipseClamp(num);
        }
      }
    }
  };

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (rotationInterval) {
        clearInterval(rotationInterval);
      }
    };
  }, [rotationInterval]);

  // Hold-to-repeat for Y position ▲/▼ buttons
  const positionYStepIntervalRef = useRef(null);
  const positionYCurrentRef = useRef(null);

  const applyYStep = (direction) => {
    const cur =
      positionYCurrentRef.current ??
      (Number.isFinite(selectedCircle?.y)
        ? Number(selectedCircle.y)
        : MIN_Y_CONSTRAINED);
    const next =
      direction === 1
        ? Math.min(MAX_Y_CONSTRAINED, cur + 1)
        : Math.max(MIN_Y_CONSTRAINED, cur - 1);
    positionYCurrentRef.current = next;
    updateCircleProperties({ y: next });
    setLocalInputValues((prev) => ({ ...prev, y: next }));
  };

  const startYStep = (direction) => {
    if (positionYStepIntervalRef.current) {
      clearInterval(positionYStepIntervalRef.current);
      positionYStepIntervalRef.current = null;
    }
    positionYCurrentRef.current = Number.isFinite(selectedCircle?.y)
      ? Number(selectedCircle.y)
      : MIN_Y_CONSTRAINED;
    applyYStep(direction);
    positionYStepIntervalRef.current = setInterval(
      () => applyYStep(direction),
      100,
    );
  };

  const stopYStep = () => {
    if (positionYStepIntervalRef.current) {
      clearInterval(positionYStepIntervalRef.current);
      positionYStepIntervalRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (positionYStepIntervalRef.current)
        clearInterval(positionYStepIntervalRef.current);
    };
  }, []);

  // Update local state when selectedCircle changes
  useEffect(() => {
    if (selectedCircle) {
      const isNewCircle = lastCircleIdRef.current !== selectedCircle.id;
      lastCircleIdRef.current = selectedCircle.id;

      // Reset changingText flag when selecting a new circle to ensure clean state
      if (isNewCircle && showTextEditor) {
        // If switching to a new circle while text editor is open, close it first
        setShowTextEditor(false);
        updateCircleProperties({ changingText: false });
      }

      // Normalize rotation to 0-360 range before setting local state
      let normalizedRotation =
        selectedCircle.rotation !== undefined ? selectedCircle.rotation : 0;
      normalizedRotation = normalizedRotation % 360;
      if (normalizedRotation < 0) {
        normalizedRotation += 360;
      }

      // Check if there's currently a text selection in the editor
      const sel = window.getSelection && window.getSelection();
      const hasActiveSelection =
        sel && sel.rangeCount > 0 && !sel.isCollapsed
          ? sel.anchorNode?.parentElement?.closest('[contenteditable="true"]')
          : null;

      // Recalculate constraints for this circle
      // Note: Negative x values are allowed for main canvas stage elements
      // CRITICAL FIX: Check _isInOffCanvasDuringDrag flag first (for elements being dragged between canvases)
      // If flag is explicitly false, element is on main canvas (don't check x position)
      // If flag is true, element is on off-canvas
      // If flag is undefined, fall back to x position check for elements already on off-canvas
      const currentIsOffCanvas =
        selectedCircle?._previewTargetStage === "offCanvas"
          ? true
          : selectedCircle?._previewTargetStage === "main"
            ? false
            : (selectedCircle?._isDragging === true &&
                selectedCircle?.isOffCanvas === true) ||
              (selectedCircle?._isDragging === true &&
                selectedCircle?._previewTargetStage !== "main" &&
                (selectedCircle?.isOffCanvas === true ||
                  selectedCircle?._isInOffCanvasDuringDrag === true ||
                  (selectedCircle?._isInOffCanvasDuringDrag === undefined &&
                    selectedCircle?.x !== undefined &&
                    selectedCircle?.x >= mainStageW))) ||
              (selectedCircle?._isDragging !== true &&
                (selectedCircle?.isOffCanvas === true ||
                  selectedCircle?._isInOffCanvasDuringDrag === true ||
                  (selectedCircle?._isInOffCanvasDuringDrag === undefined &&
                    selectedCircle?.x !== undefined &&
                    selectedCircle?.x >= mainStageW)));
      const currentConstraints = currentIsOffCanvas
        ? (() => {
            const stageWidth = getOffCanvasStageWidthPx();
            const stageHeight = getCanvasStageHeightPx();
            const radius =
              selectedCircle?.radius ||
              Math.max(
                selectedCircle?.radiusX || 0,
                selectedCircle?.radiusY || 0,
              ) ||
              0;
            const strokeHalf = Math.max(
              0,
              Number(selectedCircle?.strokeWidth || 0) / 2,
            );
            const edgeInset = EDITOR_EDGE_BUFFER_PX;
            return {
              minX: radius + strokeHalf + edgeInset,
              maxX: Math.max(
                radius + strokeHalf + edgeInset,
                stageWidth - radius - strokeHalf - edgeInset,
              ),
              minY: radius + strokeHalf + edgeInset,
              maxY: Math.max(
                radius + strokeHalf + edgeInset,
                stageHeight - radius - strokeHalf - edgeInset,
              ),
            };
          })()
        : (() => {
            // Main canvas constraints - allow negative x values
            // For circles, x and y are center coordinates
            const radius =
              selectedCircle?.radius ||
              Math.max(
                selectedCircle?.radiusX || 0,
                selectedCircle?.radiusY || 0,
              ) ||
              0;
            const canvasPixelWidth = mainStageW;
            const canvasPixelHeight = resolveCanvasHeightPx(canvasHeight);
            const strokeHalf = Math.max(
              0,
              Number(selectedCircle?.strokeWidth || 0) / 2,
            );
            const edgeInset = EDITOR_EDGE_BUFFER_PX;
            return {
              minX: -10000, // Allow negative x values for main canvas stage elements (center coordinate)
              maxX: Math.max(
                radius + strokeHalf + edgeInset,
                canvasPixelWidth - radius - strokeHalf - edgeInset,
              ),
              minY: radius + strokeHalf + edgeInset,
              maxY: Math.max(
                radius + strokeHalf + edgeInset,
                canvasPixelHeight - radius - strokeHalf - edgeInset,
              ),
            };
          })();

      setLocalInputValues((prev) => {
        // Constrain x and y to be within bounds (circles use center coordinates, x can be negative for main canvas)
        const constrainedX =
          selectedCircle.x !== undefined && selectedCircle.x !== null
            ? Math.max(
                currentConstraints.minX,
                Math.min(currentConstraints.maxX, Math.round(selectedCircle.x)),
              )
            : "";
        const constrainedY =
          selectedCircle.y !== undefined && selectedCircle.y !== null
            ? Math.max(
                currentConstraints.minY,
                Math.min(currentConstraints.maxY, Math.round(selectedCircle.y)),
              )
            : "";

        const updates = {
          x: constrainedX,
          y: constrainedY,
          radius: selectedCircle.radius
            ? Math.round(selectedCircle.radius)
            : "",
          radiusX: selectedCircle.radiusX
            ? Math.round(selectedCircle.radiusX)
            : selectedCircle.radius
              ? Math.round(selectedCircle.radius)
              : "",
          radiusY: selectedCircle.radiusY
            ? Math.round(selectedCircle.radiusY)
            : selectedCircle.radius
              ? Math.round(selectedCircle.radius)
              : "",
          rotation: Math.round(normalizedRotation),
          strokeWidth: selectedCircle.strokeWidth
            ? Math.round(selectedCircle.strokeWidth)
            : "",
          fillOpacity:
            selectedCircle.fillOpacity !== undefined
              ? Math.round(selectedCircle.fillOpacity * 100)
              : "",
          strokeOpacity:
            selectedCircle.strokeOpacity !== undefined
              ? Math.round(selectedCircle.strokeOpacity * 100)
              : "",
          strokeColorHex: selectedCircle.stroke
            ? rgbaToHex(selectedCircle.stroke).toUpperCase().replace("#", "")
            : "000000",
        };

        // Only reset textColorHex if:
        // 1. This is a new circle (different ID), OR
        // 2. There's no active selection in the editor
        // Otherwise, preserve the current value to allow selection color detection to update it
        if (isNewCircle || !hasActiveSelection) {
          const newColorHex = selectedCircle.textColor
            ? selectedCircle.textColor.toUpperCase().replace("#", "")
            : "000000";
          updates.textColorHex = newColorHex;
        } else {
          // Keep current value or fall back to circle's color if not set
          const preservedColor =
            prev.textColorHex ||
            (selectedCircle.textColor
              ? selectedCircle.textColor.toUpperCase().replace("#", "")
              : "000000");
          updates.textColorHex = preservedColor;
        }

        return { ...prev, ...updates };
      });
      setMaintainAspectRatio(selectedCircle.maintainAspectRatio ?? true);
    }
  }, [
    selectedCircle,
    selectedCircle?._isInOffCanvasDuringDrag,
    selectedCircle?.x,
    selectedCircle?.y,
    selectedCircle?.rotation,
    selectedCircle?.id,
  ]);

  const x = Number.isFinite(selectedCircle.x)
    ? Math.round(selectedCircle.x)
    : 0;
  const y = Number.isFinite(selectedCircle.y)
    ? Math.round(selectedCircle.y)
    : 0;
  const radius = Number.isFinite(selectedCircle.radius)
    ? Math.round(selectedCircle.radius)
    : 50;
  const radiusX = Number.isFinite(selectedCircle.radiusX)
    ? Math.round(selectedCircle.radiusX)
    : radius;
  const radiusY = Number.isFinite(selectedCircle.radiusY)
    ? Math.round(selectedCircle.radiusY)
    : radius;
  const fill = selectedCircle.fill || selectedCircle.fillColor || "#000000";
  const fillOpacity = selectedCircle.fillOpacity ?? 1;
  const rotation = (() => {
    let rot = Number.isFinite(selectedCircle.rotation)
      ? selectedCircle.rotation
      : 0;
    // Normalize to 0-360 range
    rot = rot % 360;
    if (rot < 0) {
      rot += 360;
    }
    return Math.round(rot);
  })();
  const strokeColor = selectedCircle.stroke || "#000000";
  const strokeWidth = Number.isFinite(selectedCircle.strokeWidth)
    ? selectedCircle.strokeWidth
    : 1;

  const textContent = selectedCircle.text || "";
  const fontSize = selectedCircle.fontSize || 15;
  const textColor = selectedCircle.textColor || "#000000";
  const fontStyle = selectedCircle.fontStyle || "normal";
  const textDecoration = selectedCircle.textDecoration || "none";
  const align = selectedCircle.align || "center";
  const verticalAlign = selectedCircle.verticalAlign || "middle";
  const text = selectedCircle.text || "";

  // Initialize font size input value after fontSize is declared
  const [fontSizeInputValue, setFontSizeInputValue] = useState(fontSize);

  // Update font size input value when global font size changes
  useEffect(() => {
    setFontSizeInputValue(fontSize);
  }, [fontSize]);

  // Helper function to get font size from entire editor content (when no selection)
  const getEditorFontSizeFromDOM = () => {
    try {
      const editorElement = document.querySelector('[contenteditable="true"]');
      if (!editorElement) return null;

      // Get all text nodes in the editor
      const walker = document.createTreeWalker(
        editorElement,
        NodeFilter.SHOW_TEXT,
        null,
      );

      const fontSizes = new Set();
      let node = walker.nextNode();

      // Collect font sizes from all text nodes
      while (node) {
        let element = node.parentElement;
        while (element && element !== editorElement) {
          // Check inline style first
          const inlineStyle = element.getAttribute("style");
          if (inlineStyle) {
            const sizeMatch = inlineStyle.match(/font-size:\s*([^;]+)/);
            if (sizeMatch && sizeMatch[1]) {
              const sizeStr = sizeMatch[1].trim();
              const pxMatch = sizeStr.match(/(\d+(?:\.\d+)?)px/);
              if (pxMatch) {
                fontSizes.add(parseInt(pxMatch[1], 10));
                break; // Found inline style, no need to go up further
              }
            }
          }

          // Check computed style
          const computedStyle = window.getComputedStyle(element);
          const fontSize = computedStyle.fontSize;
          if (fontSize) {
            const pxMatch = fontSize.match(/(\d+(?:\.\d+)?)px/);
            if (pxMatch) {
              fontSizes.add(parseInt(pxMatch[1], 10));
            }
          }

          element = element.parentElement;
        }
        node = walker.nextNode();
      }

      // Return the most common font size, or max if multiple
      if (fontSizes.size > 0) {
        const sizes = Array.from(fontSizes);
        return Math.max(...sizes); // Return max font size found
      }

      // Fallback: check editor element's computed style
      const editorStyle = window.getComputedStyle(editorElement);
      const editorFontSize = editorStyle.fontSize;
      if (editorFontSize) {
        const pxMatch = editorFontSize.match(/(\d+(?:\.\d+)?)px/);
        if (pxMatch) {
          return parseInt(pxMatch[1], 10);
        }
      }

      return null;
    } catch (e) {
      console.error("Error getting font size from editor DOM:", e);
      return null;
    }
  };

  // Detect font size from editor content when text editor opens
  useEffect(() => {
    if (!showTextEditor) return;

    // Wait a bit for the editor to be rendered
    const timeoutId = setTimeout(() => {
      // Try to get font size from Lexical first
      const fontInfo = window.lexicalSelectedFontInfo;
      if (fontInfo && Number.isFinite(fontInfo.max)) {
        setFontSizeInputValue(fontInfo.max);
        return;
      }

      // Fallback: get from DOM
      const editorFontSize = getEditorFontSizeFromDOM();
      if (Number.isFinite(editorFontSize)) {
        setFontSizeInputValue(editorFontSize);
        return;
      }

      // Final fallback: use fontSize prop
      if (Number.isFinite(fontSize)) {
        setFontSizeInputValue(fontSize);
      }
    }, 100); // Small delay to ensure editor is rendered

    return () => clearTimeout(timeoutId);
  }, [showTextEditor, fontSize]);

  // Helper function to get font size from DOM (moved outside useEffect for accessibility)
  const getSelectedFontSizeFromDOM = () => {
    try {
      const sel = window.getSelection && window.getSelection();
      if (!sel || sel.rangeCount === 0) return null;

      const range = sel.getRangeAt(0);
      const editorElement = document.querySelector('[contenteditable="true"]');
      if (!editorElement) return null;

      // Check if selection is within the editor
      if (!editorElement.contains(range.commonAncestorContainer)) return null;

      // Get all text nodes in the range
      const walker = document.createTreeWalker(
        range.commonAncestorContainer,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode: (node) => {
            return range.intersectsNode(node)
              ? NodeFilter.FILTER_ACCEPT
              : NodeFilter.FILTER_REJECT;
          },
        },
      );

      const fontSizes = new Set();
      let node = walker.nextNode();

      // Collect font sizes from all selected text nodes
      while (node) {
        let element = node.parentElement;
        while (element && element !== editorElement) {
          // Check inline style first
          const inlineStyle = element.getAttribute("style");
          if (inlineStyle) {
            const sizeMatch = inlineStyle.match(/font-size:\s*([^;]+)/);
            if (sizeMatch && sizeMatch[1]) {
              const sizeStr = sizeMatch[1].trim();
              const pxMatch = sizeStr.match(/(\d+(?:\.\d+)?)px/);
              if (pxMatch) {
                fontSizes.add(parseInt(pxMatch[1], 10));
                break; // Found inline style, no need to go up further
              }
            }
          }

          // Check computed style
          const computedStyle = window.getComputedStyle(element);
          const fontSize = computedStyle.fontSize;
          if (fontSize) {
            const pxMatch = fontSize.match(/(\d+(?:\.\d+)?)px/);
            if (pxMatch) {
              fontSizes.add(parseInt(pxMatch[1], 10));
            }
          }

          element = element.parentElement;
        }
        node = walker.nextNode();
      }

      // Return the most common font size, or max if multiple
      if (fontSizes.size > 0) {
        const sizes = Array.from(fontSizes);
        return Math.max(...sizes); // Return max font size found
      }

      // Fallback: check anchor node
      let anchorNode = sel.anchorNode;
      if (anchorNode) {
        if (anchorNode.nodeType === 3) anchorNode = anchorNode.parentElement;
        if (anchorNode instanceof Element) {
          const withinEditor = anchorNode.closest('[contenteditable="true"]');
          if (withinEditor) {
            const style = window.getComputedStyle(anchorNode);
            const px = parseInt(style.fontSize, 10);
            if (Number.isFinite(px)) return px;
          }
        }
      }

      return null;
    } catch (e) {
      console.error("Error getting font size from DOM:", e);
      return null;
    }
  };

  // Autofill font size input with selected text's font size when selection changes
  useEffect(() => {
    const updateFromSelection = () => {
      // Early return if listeners are disabled or we're programmatically changing
      if (!selectionUpdateListenersRef.current.enabled) return;
      if (isProgrammaticallyChangingFontSizeRef.current) return;

      const inputIsFocused =
        document.activeElement &&
        document.activeElement.getAttribute &&
        (document.activeElement.getAttribute("data-font-size-input") ||
          document.activeElement.getAttribute("data-color-input"));
      if (inputIsFocused) return;

      // Check if there's actually a selection in the editor
      const sel = window.getSelection && window.getSelection();
      const hasSelection = sel && sel.rangeCount > 0 && !sel.isCollapsed;
      if (!hasSelection) {
        // No selection - don't update when programmatically changing
        // This prevents interference during arrow key changes
        if (isProgrammaticallyChangingFontSizeRef.current) return;

        // Try to get font size from entire editor content
        const editorFontSize = getEditorFontSizeFromDOM();
        if (Number.isFinite(editorFontSize)) {
          setFontSizeInputValue(editorFontSize);
          return;
        }
        // If we can't detect from editor, keep current value
        return;
      }

      // Check if selection is within the editor
      const editorElement = document.querySelector('[contenteditable="true"]');
      if (!editorElement) return;

      const range = sel.getRangeAt(0);
      if (!editorElement.contains(range.commonAncestorContainer)) return;

      // Prefer Lexical selection info when available
      const fontInfo = window.lexicalSelectedFontInfo;
      if (fontInfo) {
        // If mixed, try to get from DOM first before clearing
        if (fontInfo.isMixed) {
          const domSize = getSelectedFontSizeFromDOM();
          if (Number.isFinite(domSize)) {
            setFontSizeInputValue(domSize);
            return;
          }
          // Only clear if we can't detect from DOM either
          setFontSizeInputValue("");
          return;
        }
        if (Number.isFinite(fontInfo.max)) {
          setFontSizeInputValue(fontInfo.max);
          return;
        }
      }

      // Fallback: DOM-computed size (this is more reliable)
      const domSize = getSelectedFontSizeFromDOM();
      if (Number.isFinite(domSize)) {
        setFontSizeInputValue(domSize);
        return;
      }

      if (window.selectedTextInfo && window.selectedTextInfo.text?.length > 0) {
        const parsed = getCurrentFontSize();
        if (Number.isFinite(parsed)) {
          setFontSizeInputValue(parsed);
          return;
        }
      }

      // If we have a selection but no font info, try to get from computed style
      try {
        const range = sel.getRangeAt(0);
        const container = range.commonAncestorContainer;
        let element =
          container.nodeType === Node.TEXT_NODE
            ? container.parentElement
            : container;
        const editorElement = element?.closest('[contenteditable="true"]');
        if (editorElement && element) {
          const computedStyle = window.getComputedStyle(element);
          const fontSizePx = computedStyle.fontSize;
          if (fontSizePx) {
            const match = fontSizePx.match(/(\d+(?:\.\d+)?)px/);
            if (match) {
              const size = parseInt(match[1], 10);
              if (Number.isFinite(size)) {
                setFontSizeInputValue(size);
                return;
              }
            }
          }
        }
      } catch (e) {
        // Ignore errors
      }
    };

    // Listen for selection changes
    document.addEventListener("selectionchange", updateFromSelection);
    window.addEventListener("lexical-selection-change", updateFromSelection);

    // Also listen for Lexical editor updates to catch formatting changes
    window.addEventListener("lexical-editor-update", updateFromSelection);

    // Poll for font size changes when editor is active (fallback)
    const intervalId = setInterval(() => {
      // Don't poll if we're programmatically changing font size
      if (isProgrammaticallyChangingFontSizeRef.current) return;

      // Check if any input is focused
      const activeElement = document.activeElement;
      const inputIsFocused =
        activeElement &&
        activeElement.getAttribute &&
        (activeElement.getAttribute("data-font-size-input") ||
          activeElement.getAttribute("data-color-input"));

      // Only update if input is not focused and there's a selection
      if (!inputIsFocused) {
        const sel = window.getSelection && window.getSelection();
        const hasSelection = sel && sel.rangeCount > 0 && !sel.isCollapsed;
        const editorElement = document.querySelector(
          '[contenteditable="true"]',
        );

        // Check if selection is within the editor
        if (hasSelection && editorElement) {
          const range = sel.getRangeAt(0);
          if (editorElement.contains(range.commonAncestorContainer)) {
            updateFromSelection();
          }
        }
      }
    }, 50); // Check every 50ms for more responsive updates

    return () => {
      document.removeEventListener("selectionchange", updateFromSelection);
      window.removeEventListener(
        "lexical-selection-change",
        updateFromSelection,
      );
      window.removeEventListener("lexical-editor-update", updateFromSelection);
      clearInterval(intervalId);
    };
  }, [fontSize, formattingUpdate]);

  // Update color hex input when text selection changes
  useEffect(() => {
    const getSelectedTextColorFromDOM = () => {
      try {
        const sel = window.getSelection && window.getSelection();
        if (!sel || sel.rangeCount === 0) {
          return null;
        }

        // Try to get color from the actual styled element
        const range = sel.getRangeAt(0);
        if (!range) {
          return null;
        }

        // Get the common ancestor container
        const container = range.commonAncestorContainer;
        let element =
          container.nodeType === Node.TEXT_NODE
            ? container.parentElement
            : container;

        // Walk up to find an element with a style attribute
        while (element && element.nodeType === Node.ELEMENT_NODE) {
          // Check if element is within the editor
          const withinEditor = element.closest('[contenteditable="true"]');
          if (!withinEditor) break;

          // Check inline style attribute first
          const inlineStyle = element.getAttribute("style");
          if (inlineStyle) {
            const colorMatch = inlineStyle.match(/color:\s*([^;]+)/);
            if (colorMatch && colorMatch[1]) {
              const color = colorMatch[1].trim();
              return color;
            }
          }

          element = element.parentElement;
        }

        // Fallback to computed style on the selection's container
        let node = sel.anchorNode;
        if (!node) {
          return null;
        }
        if (node.nodeType === 3) node = node.parentElement;
        if (!(node instanceof Element)) {
          return null;
        }
        const withinEditor = node.closest('[contenteditable="true"]');
        if (!withinEditor) {
          return null;
        }
        const style = window.getComputedStyle(node);
        const color = style.color;
        // Return the color even if it's black - black is a valid color
        // The issue was that we were returning null for black, which caused it to fall back to default
        if (color) {
          return color;
        }
        return null;
      } catch (error) {
        return null;
      }
    };

    const updateColorFromSelection = () => {
      const inputIsFocused =
        document.activeElement &&
        document.activeElement.getAttribute &&
        document.activeElement.getAttribute("data-color-input");

      // Check if there's actually a selection
      const sel = window.getSelection && window.getSelection();
      const hasSelection = sel && sel.rangeCount > 0 && !sel.isCollapsed;

      if (inputIsFocused) {
        return;
      }

      // If there's no selection, don't update - preserve current value
      if (!hasSelection) {
        return;
      }

      // Try to get color from Lexical editor if available
      let selectedColor = null;

      // Prefer Lexical selection color info if available (parsed from node styles)
      // This is the most accurate source as it comes directly from the selected nodes
      if (
        window.lexicalSelectedColorInfo &&
        window.lexicalSelectedColorInfo.color
      ) {
        selectedColor = window.lexicalSelectedColorInfo.color;
      } else if (window.getLexicalFormattingState) {
        // Fallback to Lexical formatting state API - but be careful with defaults
        try {
          const formatting = window.getLexicalFormattingState();
          if (formatting && formatting.textColor) {
            const formatColor = formatting.textColor;
            // Don't use #000000 from formatting state if lexicalSelectedColorInfo is not set
            // This is likely a default/fallback value, not the actual selected color
            // Only trust formatting state if it's not black, or wait for lexicalSelectedColorInfo
            if (formatColor !== "#000000" && formatColor !== "rgb(0, 0, 0)") {
              selectedColor = formatColor;
            } else {
              // Don't set selectedColor, let it fall through to DOM detection
            }
          }
        } catch (e) {
          // Fallback to DOM
        }
      }

      // Fallback: DOM-computed color (only if Lexical doesn't provide it)
      if (!selectedColor) {
        selectedColor = getSelectedTextColorFromDOM();
      }

      // Only update if we have a valid selected color
      if (selectedColor) {
        // Convert to hex if needed
        let hexColor = selectedColor;
        if (selectedColor.startsWith("rgb")) {
          // Convert rgb/rgba to hex
          const rgbMatch = selectedColor.match(/\d+/g);
          if (rgbMatch && rgbMatch.length >= 3) {
            const r = parseInt(rgbMatch[0], 10);
            const g = parseInt(rgbMatch[1], 10);
            const b = parseInt(rgbMatch[2], 10);
            hexColor = `#${((1 << 24) + (r << 16) + (g << 8) + b)
              .toString(16)
              .slice(1)
              .toUpperCase()}`;
          }
        } else if (!selectedColor.startsWith("#")) {
          // If it's a named color or other format, try to get computed style
          try {
            const div = document.createElement("div");
            div.style.color = selectedColor;
            document.body.appendChild(div);
            const computed = window.getComputedStyle(div).color;
            document.body.removeChild(div);
            const rgbMatch = computed.match(/\d+/g);
            if (rgbMatch && rgbMatch.length >= 3) {
              const r = parseInt(rgbMatch[0], 10);
              const g = parseInt(rgbMatch[1], 10);
              const b = parseInt(rgbMatch[2], 10);
              hexColor = `#${((1 << 24) + (r << 16) + (g << 8) + b)
                .toString(16)
                .slice(1)
                .toUpperCase()}`;
            }
          } catch (e) {
            // Fallback to default
            hexColor = textColor;
          }
        }

        // Update local state with hex (without #)
        const hexWithoutHash = hexColor.toUpperCase().replace("#", "");
        setLocalInputValues((prev) => {
          const newValues = {
            ...prev,
            textColorHex: hexWithoutHash,
          };
          return newValues;
        });
      } else {
        // No selected color found, but we have a selection
        // Don't update - preserve current value to avoid resetting to default
      }
    };

    document.addEventListener("selectionchange", updateColorFromSelection);
    window.addEventListener(
      "lexical-selection-change",
      updateColorFromSelection,
    );
    return () => {
      document.removeEventListener("selectionchange", updateColorFromSelection);
      window.removeEventListener(
        "lexical-selection-change",
        updateColorFromSelection,
      );
    };
  }, [textColor, formattingUpdate]);

  // Function to get current font size for display
  const getCurrentFontSize = () => {
    // If we're programmatically changing, don't read from Lexical/DOM - use input value
    if (isProgrammaticallyChangingFontSizeRef.current) {
      return Number.isFinite(fontSizeInputValue)
        ? fontSizeInputValue
        : fontSize;
    }

    // If the input is focused, use the input value to prevent losing focus
    if (
      document.activeElement &&
      document.activeElement.type === "number" &&
      document.activeElement.getAttribute("data-font-size-input")
    ) {
      return fontSizeInputValue;
    }

    // Prefer Lexical selection info (only if not programmatically changing)
    const fontInfo = window.lexicalSelectedFontInfo;
    if (fontInfo) {
      if (fontInfo.isMixed) return fontInfo.max; // start from max when editing
      if (Number.isFinite(fontInfo.max)) return fontInfo.max;
    }

    if (window.selectedTextInfo && window.selectedTextInfo.text.length > 0) {
      const selectedText = window.selectedTextInfo.text;
      const fontSizeMatch = selectedText.match(
        /<fontsize[^>]*style="font-size:\s*(\d+)px"[^>]*>/,
      );
      if (fontSizeMatch) return parseInt(fontSizeMatch[1], 10);
      const wrappedMatch = selectedText.match(
        /<fontsize[^>]*style="font-size:\s*(\d+)px"[^>]*>(.*?)<\/fontsize>/,
      );
      if (wrappedMatch) return parseInt(wrappedMatch[1], 10);
    }
    return fontSize;
  };

  // Always use MIN_FONT_SIZE as the minimum constraint
  const dynamicMinFontSize = MIN_FONT_SIZE;

  const handleNumberInput = (field, value) => {
    const num = parseInt(value, 10);
    if (!isNaN(num)) {
      updateCircleProperties({ [field]: num });
    }
  };

  const rotate = (direction) => {
    if (!selectedCircle) return;

    const oldRotation = selectedCircle.rotation || 0;
    let newRotation = oldRotation + direction;

    // Normalize to 0-360 range
    newRotation = newRotation % 360;
    if (newRotation < 0) {
      newRotation += 360;
    }

    // Keep ellipse bounds valid when rotation changes.
    applyRotationWithEllipseClamp(newRotation);
    setLocalInputValues((prev) => ({
      ...prev,
      rotation: Math.round(newRotation),
    }));
  };

  const handleAspectRatioChange = (checked) => {
    setMaintainAspectRatio(checked);

    if (checked) {
      // Switching to circle mode - calculate equivalent radius that preserves the area
      // Area of ellipse = π * radiusX * radiusY
      // Area of circle = π * radius²
      // So: radius = √(radiusX * radiusY)
      // Alternative approaches:
      // - Average: (radiusX + radiusY) / 2 (preserves average size)
      // - Max: Math.max(radiusX, radiusY) (preserves largest dimension)
      // - Min: Math.min(radiusX, radiusY) (preserves smallest dimension)
      const equivalentRadius = Math.round(Math.sqrt(radiusX * radiusY));

      updateCircleProperties({
        maintainAspectRatio: checked,
        radius: equivalentRadius,
        radiusX: equivalentRadius,
        radiusY: equivalentRadius,
        // CRITICAL FIX: Preserve x and y to ensure transformer anchors update correctly
        x: selectedCircle.x,
        y: selectedCircle.y,
      });

      // Update local input values
      setLocalInputValues((prev) => ({
        ...prev,
        radius: equivalentRadius,
        radiusX: equivalentRadius,
        radiusY: equivalentRadius,
      }));
    } else {
      // Switching to ellipse mode - preserve current radius for both X and Y
      updateCircleProperties({
        maintainAspectRatio: checked,
        radiusX: radius,
        radiusY: radius,
        // CRITICAL FIX: Preserve x and y to ensure transformer anchors update correctly
        x: selectedCircle.x,
        y: selectedCircle.y,
      });

      // Update local input values
      setLocalInputValues((prev) => ({
        ...prev,
        radiusX: radius,
        radiusY: radius,
      }));
    }
  };

  const applyRadiiWithCenterClamp = (
    requestedRadiusX,
    requestedRadiusY,
    { keepCircleRadiusField = false, rotationOverride } = {},
  ) => {
    console.log("DEBUG: applyRadiiWithCenterClamp called:", {
      requestedRadiusX: requestedRadiusX,
      requestedRadiusY: requestedRadiusY,
      keepCircleRadiusField: keepCircleRadiusField
    });
    const toFinite = (value, fallback) => {
      const num = Number(value);
      return Number.isFinite(num) ? num : fallback;
    };
    const minRx = Math.max(0, MIN_RADIUS_X);
    const minRy = Math.max(0, MIN_RADIUS_Y);
    const rotationDeg = toFinite(
      rotationOverride !== undefined ? rotationOverride : selectedCircle?.rotation,
      0,
    );
    const rotationRad = (rotationDeg * Math.PI) / 180;
    const absCos = Math.abs(Math.cos(rotationRad));
    const absSin = Math.abs(Math.sin(rotationRad));
    const epsilon = 1e-6;
    // Use MAX_RADIUS for X but stage boundary for Y to match CircleElement behavior
    const halfStageLimitX = Math.max(0, MAX_RADIUS);
    const halfStageLimitY = Math.max(0, maxRadiusYByStageFit);
    console.log("DEBUG: applyRadiiWithCenterClamp stage limits:", {
      halfStageLimitX: halfStageLimitX,
      halfStageLimitY: halfStageLimitY
    });
    const baseMaxRx = Math.max(minRx, maxRadiusXByStageFit);
    const baseMaxRy = Math.max(minRy, maxRadiusYByStageFit);
    const safeSqrt = (value) => Math.sqrt(Math.max(0, value));
    const getMaxRxForCurrentRy = (ryValue) => {
      const safeRy = Math.max(minRy, toFinite(ryValue, minRy));
      const rySin = absSin * safeRy;
      const ryCos = absCos * safeRy;
      const byX =
        absCos > epsilon
          ? safeSqrt(halfStageLimitX * halfStageLimitX - rySin * rySin) /
            absCos
          : halfStageLimitX >= rySin
            ? Number.POSITIVE_INFINITY
            : -1;
      const byY =
        absSin > epsilon
          ? safeSqrt(halfStageLimitY * halfStageLimitY - ryCos * ryCos) /
            absSin
          : halfStageLimitY >= ryCos
            ? Number.POSITIVE_INFINITY
            : -1;
      return Math.max(minRx, Math.min(baseMaxRx, byX, byY));
    };
    const getMaxRyForCurrentRx = (rxValue) => {
      const safeRx = Math.max(minRx, toFinite(rxValue, minRx));
      const rxCos = absCos * safeRx;
      const rxSin = absSin * safeRx;
      const byX =
        absSin > epsilon
          ? safeSqrt(halfStageLimitX * halfStageLimitX - rxCos * rxCos) /
            absSin
          : halfStageLimitX >= rxCos
            ? Number.POSITIVE_INFINITY
            : -1;
      const byY =
        absCos > epsilon
          ? safeSqrt(halfStageLimitY * halfStageLimitY - rxSin * rxSin) /
            absCos
          : halfStageLimitY >= rxSin
            ? Number.POSITIVE_INFINITY
            : -1;
      return Math.max(minRy, Math.min(baseMaxRy, byX, byY));
    };

    // Clamp Y first against requested X, then clamp X against resulting Y.
    // This avoids collapsing radiusX to MIN when radiusY is temporarily oversized.
    const requestedRx = Math.max(
      minRx,
      Math.min(baseMaxRx, toFinite(requestedRadiusX, minRx)),
    );
    let ry = Math.max(
      minRy,
      Math.min(
        getMaxRyForCurrentRx(requestedRx),
        Math.min(baseMaxRy, toFinite(requestedRadiusY, minRy)),
      ),
    );
    let rx = Math.max(
      minRx,
      Math.min(getMaxRxForCurrentRy(ry), requestedRx),
    );

    const currentX = Number(selectedCircle?.x) || 0;
    const currentY = Number(selectedCircle?.y) || 0;

    const minCenterX = rx + strokeHalf + EDITOR_EDGE_BUFFER_PX;
    const maxCenterX =
      effectiveRadiusCapStageWidth - rx - strokeHalf - EDITOR_EDGE_BUFFER_PX;
    const minCenterY = ry + strokeHalf + EDITOR_EDGE_BUFFER_PX;
    const maxCenterY = activeStageHeight - ry - strokeHalf - EDITOR_EDGE_BUFFER_PX;

    const clampedX =
      minCenterX > maxCenterX
        ? effectiveRadiusCapStageWidth / 2
        : Math.max(minCenterX, Math.min(maxCenterX, currentX));
    const clampedY =
      minCenterY > maxCenterY
        ? activeStageHeight / 2
        : Math.max(minCenterY, Math.min(maxCenterY, currentY));

    const payload = {
      radiusX: rx,
      radiusY: ry,
      x: clampedX,
      y: clampedY,
    };

    if (keepCircleRadiusField) {
      payload.radius = Math.min(rx, ry);
    }

    updateCircleProperties(payload);
    return { rx, ry };
  };

  const applyRotationWithEllipseClamp = (nextRotationRaw) => {
    let normalizedRotation = Number(nextRotationRaw);
    if (!Number.isFinite(normalizedRotation)) {
      normalizedRotation = 0;
    }
    normalizedRotation = normalizedRotation % 360;
    if (normalizedRotation < 0) {
      normalizedRotation += 360;
    }

    const currentRadiusX = Number.isFinite(Number(selectedCircle?.radiusX))
      ? Number(selectedCircle.radiusX)
      : Number.isFinite(Number(selectedCircle?.radius))
        ? Number(selectedCircle.radius)
        : MIN_RADIUS_X;
    const currentRadiusY = Number.isFinite(Number(selectedCircle?.radiusY))
      ? Number(selectedCircle.radiusY)
      : Number.isFinite(Number(selectedCircle?.radius))
        ? Number(selectedCircle.radius)
        : MIN_RADIUS_Y;

    // Rotation should not mutate size. Preserve radii and only clamp center
    // so the rotated ellipse stays inside stage bounds.
    const rotationRad = (normalizedRotation * Math.PI) / 180;
    const absCos = Math.abs(Math.cos(rotationRad));
    const absSin = Math.abs(Math.sin(rotationRad));
    const rotatedHalfWidth = Math.sqrt(
      (currentRadiusX * absCos) ** 2 + (currentRadiusY * absSin) ** 2,
    );
    const rotatedHalfHeight = Math.sqrt(
      (currentRadiusX * absSin) ** 2 + (currentRadiusY * absCos) ** 2,
    );
    const minCenterX = rotatedHalfWidth + strokeHalf + EDITOR_EDGE_BUFFER_PX;
    const maxCenterX =
      effectiveRadiusCapStageWidth -
      rotatedHalfWidth -
      strokeHalf -
      EDITOR_EDGE_BUFFER_PX;
    const minCenterY = rotatedHalfHeight + strokeHalf + EDITOR_EDGE_BUFFER_PX;
    const maxCenterY =
      activeStageHeight - rotatedHalfHeight - strokeHalf - EDITOR_EDGE_BUFFER_PX;
    const currentX = Number(selectedCircle?.x) || 0;
    const currentY = Number(selectedCircle?.y) || 0;
    const clampedX =
      minCenterX > maxCenterX
        ? effectiveRadiusCapStageWidth / 2
        : Math.max(minCenterX, Math.min(maxCenterX, currentX));
    const clampedY =
      minCenterY > maxCenterY
        ? activeStageHeight / 2
        : Math.max(minCenterY, Math.min(maxCenterY, currentY));

    updateCircleProperties({
      rotation: normalizedRotation,
      x: clampedX,
      y: clampedY,
      radiusX: currentRadiusX,
      radiusY: currentRadiusY,
      ...(maintainAspectRatio
        ? { radius: Number(selectedCircle?.radius) || Math.min(currentRadiusX, currentRadiusY) }
        : {}),
    });
  };

  const handleRadiusXChange = (newRadiusX) => {
    console.log("DEBUG: handleRadiusXChange called:", {
      newRadiusX: newRadiusX,
      maintainAspectRatio: maintainAspectRatio,
      maxRadiusByStageFit: maxRadiusByStageFit
    });
    if (maintainAspectRatio) {
      const r = Math.max(
        MIN_RADIUS,
        Math.min(maxRadiusByStageFit, Number(newRadiusX) || MIN_RADIUS),
      );
      console.log("DEBUG: handleRadiusXChange maintainAspectRatio final r:", r);
      applyRadiiWithCenterClamp(r, r, { keepCircleRadiusField: true });
    } else {
      console.log("DEBUG: handleRadiusXChange calling applyRadiiWithCenterClamp with:", newRadiusX, radiusY);
      applyRadiiWithCenterClamp(newRadiusX, radiusY);
    }
  };

  const handleRadiusYChange = (newRadiusY) => {
    if (maintainAspectRatio) {
      const r = Math.max(
        MIN_RADIUS,
        Math.min(maxRadiusYByStageFit, Number(newRadiusY) || MIN_RADIUS),
      );
      applyRadiiWithCenterClamp(r, r, { keepCircleRadiusField: true });
    } else {
      applyRadiiWithCenterClamp(radiusX, newRadiusY);
    }
  };

  // Helper to validate and parse hex color
  const validateAndParseHexColor = (hexValue) => {
    // Remove # if present and trim whitespace
    let hex = hexValue.trim().replace(/^#/, "").toUpperCase();

    // Validate hex: must be 3 or 6 characters, only 0-9 and A-F
    if (!/^[0-9A-F]{3}$|^[0-9A-F]{6}$/.test(hex)) {
      return null; // Invalid hex
    }

    // Convert 3-digit to 6-digit if needed
    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((char) => char + char)
        .join("");
    }

    return `#${hex}`;
  };

  const handleTextColorChange = (e) => {
    const newColor = e.target.value;

    // Update local hex input to stay in sync
    setLocalInputValues((prev) => ({
      ...prev,
      textColorHex: newColor.toUpperCase().replace("#", ""),
    }));

    // Use Lexical text color if available
    if (window.applyLexicalTextColor) {
      window.applyLexicalTextColor(newColor);
      return;
    }

    // Fallback to existing text-based formatting
    if (window.selectedTextInfo && window.selectedTextInfo.text.length > 0) {
      const { start, end, element } = window.selectedTextInfo;

      // Get the current formatted text
      const currentFormattedText = selectedCircle.text || "";

      // Apply color formatting to selected text
      const newFormattedText = applyColorToSelectedText(
        currentFormattedText,
        newColor,
      );

      updateCircleProperties({ text: newFormattedText });
      setFormattingUpdate((prev) => prev + 1);

      // Update cursor position
      setTimeout(() => {
        element.focus();
        element.setSelectionRange(start, end);
      }, 0);
    } else {
      // Apply to entire text - update global text color
      updateCircleProperties({ textColor: newColor });
    }
  };

  // Function to apply color formatting to selected text
  const applyColorToSelectedText = (formattedText, color) => {
    if (!window.selectedTextInfo || !window.selectedTextInfo.text.length) {
      return formattedText;
    }

    const { text: selectedText, start, end } = window.selectedTextInfo;

    // Find the selected text in the formatted content
    const beforeSelection = formattedText.substring(0, start);
    const afterSelection = formattedText.substring(end);

    // Apply color formatting to the selected text
    const coloredText = `<color style="color: ${color};">${selectedText}</color>`;

    return beforeSelection + coloredText + afterSelection;
  };

  // Function to apply color to entire text (for color swatches)
  const applyColorToEntireText = (color) => {
    // Update local hex input to stay in sync
    setLocalInputValues((prev) => ({
      ...prev,
      textColorHex: color.toUpperCase().replace("#", ""),
    }));

    // Use Lexical text color if available
    if (window.applyLexicalTextColor) {
      window.applyLexicalTextColor(color);
      return;
    }

    // Fallback to existing text-based formatting
    if (window.selectedTextInfo && window.selectedTextInfo.text.length > 0) {
      const { start, end, element } = window.selectedTextInfo;

      // Get the current formatted text
      const currentFormattedText = selectedCircle.text || "";

      // Apply color formatting to selected text
      const newFormattedText = applyColorToSelectedText(
        currentFormattedText,
        color,
      );

      updateCircleProperties({ text: newFormattedText });
      setFormattingUpdate((prev) => prev + 1);

      // Update cursor position
      setTimeout(() => {
        element.focus();
        element.setSelectionRange(start, end);
      }, 0);
    } else {
      // Apply to entire text - update global text color
      updateCircleProperties({ textColor: color });
    }
  };

  const handleFontSizeChange = (e, skipVerification = false) => {
    const newFontSize = parseInt(e.target.value, 10);

    // Set flag to prevent selection change listeners from interfering
    isProgrammaticallyChangingFontSizeRef.current = true;

    // Immediately set the input value to prevent it from being cleared
    setFontSizeInputValue(newFontSize);

    // For Lexical editor, we need to restore selection first, then apply
    if (window.applyLexicalFontSizeWithRestore) {
      // Disable selection update listeners temporarily to prevent stale value reads
      if (skipVerification) {
        selectionUpdateListenersRef.current.enabled = false;
      }

      window.applyLexicalFontSizeWithRestore(`${newFontSize}px`);

      // If this is a programmatic change (arrow keys), skip DOM verification
      // and just trust the value we set. Only verify for manual input changes.
      if (skipVerification) {
        // For programmatic changes, just keep the value we set and clear flag after a delay
        setTimeout(() => {
          isProgrammaticallyChangingFontSizeRef.current = false;
          // Re-enable listeners after a delay to let Lexical events settle
          setTimeout(() => {
            selectionUpdateListenersRef.current.enabled = true;
          }, 100);
        }, 250);
        return;
      }

      // Only verify from DOM for manual input changes (not arrow keys)
      setTimeout(() => {
        const fontInfo = window.lexicalSelectedFontInfo;
        if (fontInfo && Number.isFinite(fontInfo.max)) {
          setFontSizeInputValue(fontInfo.max);
        } else {
          // Try DOM detection as fallback
          const domSize = getSelectedFontSizeFromDOM();
          if (Number.isFinite(domSize)) {
            setFontSizeInputValue(domSize);
          } else {
            // Keep the new font size value
            setFontSizeInputValue(newFontSize);
          }
        }
        // Clear the flag after update with a small additional delay to prevent race conditions
        setTimeout(() => {
          isProgrammaticallyChangingFontSizeRef.current = false;
        }, 50);
      }, 200);

      return;
    }

    // Fallback to existing text-based formatting
    if (window.selectedTextInfo && window.selectedTextInfo.text.length > 0) {
      const { start, end, element } = window.selectedTextInfo;

      // Get the current formatted text
      const currentFormattedText = selectedCircle.text || "";

      // Apply font size formatting to selected text
      const newFormattedText = applyFontSizeToSelectedText(
        currentFormattedText,
        newFontSize,
      );

      updateCircleProperties({ text: newFormattedText });
      setFormattingUpdate((prev) => prev + 1);

      // Update cursor position
      setTimeout(() => {
        element.focus();
        element.setSelectionRange(start, end);
        // Clear the flag after update
        isProgrammaticallyChangingFontSizeRef.current = false;
      }, 0);
    } else {
      // Apply to entire text - update global font size
      updateCircleProperties({ fontSize: newFontSize });
      // Clear the flag after update
      setTimeout(() => {
        isProgrammaticallyChangingFontSizeRef.current = false;
      }, 0);
    }
  };

  // Font size increment/decrement functions that preserve focus
  const handleFontSizeIncrement = (e) => {
    // Prevent default to stop any focus changes
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Set flag to prevent selection change listeners from interfering
    isProgrammaticallyChangingFontSizeRef.current = true;

    // Use Lexical font size if available - these work directly on selection
    if (window.applyLexicalFontSizeIncrement) {
      // Use fontSizeInputValue directly instead of getCurrentFontSize() to avoid reading stale values
      const currentSize = Number.isFinite(fontSizeInputValue)
        ? fontSizeInputValue
        : fontSize;
      const newSize = Math.min(currentSize + 1, MAX_FONT_SIZE);

      // Immediately update to prevent blank value
      setFontSizeInputValue(newSize);

      // Disable selection update listeners temporarily
      selectionUpdateListenersRef.current.enabled = false;

      window.applyLexicalFontSizeIncrement();

      // For programmatic changes (buttons), don't verify from DOM
      // Just trust the value we set to avoid flickering from stale DOM reads
      // Clear the flag after a delay to allow normal updates to resume
      setTimeout(() => {
        isProgrammaticallyChangingFontSizeRef.current = false;
        // Re-enable listeners after a delay to let Lexical events settle
        setTimeout(() => {
          selectionUpdateListenersRef.current.enabled = true;
        }, 100);
      }, 250);

      return;
    }

    // Fallback to manual increment
    const currentFontSize = getCurrentFontSize();
    const newFontSize = Math.min(currentFontSize + 1, MAX_FONT_SIZE);
    handleFontSizeChange({ target: { value: newFontSize.toString() } });
  };

  const handleFontSizeDecrement = (e) => {
    // Prevent default to stop any focus changes
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Set flag to prevent selection change listeners from interfering
    isProgrammaticallyChangingFontSizeRef.current = true;

    // Use Lexical font size if available - these work directly on selection
    if (window.applyLexicalFontSizeDecrement) {
      // Use fontSizeInputValue directly instead of getCurrentFontSize() to avoid reading stale values
      const currentSize = Number.isFinite(fontSizeInputValue)
        ? fontSizeInputValue
        : fontSize;
      const newSize = Math.max(currentSize - 1, MIN_FONT_SIZE);

      // Immediately update to prevent blank value
      setFontSizeInputValue(newSize);

      // Disable selection update listeners temporarily
      selectionUpdateListenersRef.current.enabled = false;

      window.applyLexicalFontSizeDecrement();

      // For programmatic changes (buttons), don't verify from DOM
      // Just trust the value we set to avoid flickering from stale DOM reads
      // Clear the flag after a delay to allow normal updates to resume
      setTimeout(() => {
        isProgrammaticallyChangingFontSizeRef.current = false;
        // Re-enable listeners after a delay to let Lexical events settle
        setTimeout(() => {
          selectionUpdateListenersRef.current.enabled = true;
        }, 100);
      }, 250);

      return;
    }

    // Fallback to manual decrement
    const currentFontSize = getCurrentFontSize();
    const newFontSize = Math.max(currentFontSize - 1, MIN_FONT_SIZE);
    handleFontSizeChange({ target: { value: newFontSize.toString() } });
  };

  // Function to apply font size formatting to selected text
  const applyFontSizeToSelectedText = (formattedText, fontSize) => {
    if (!window.selectedTextInfo || !window.selectedTextInfo.text.length) {
      return formattedText;
    }

    const { text: selectedText, start, end } = window.selectedTextInfo;

    // Find the selected text in the formatted content
    const beforeSelection = formattedText.substring(0, start);
    const afterSelection = formattedText.substring(end);

    // Apply font size formatting to the selected text
    const sizedText = `<fontsize style="font-size: ${fontSize}px;">${selectedText}</fontsize>`;

    return beforeSelection + sizedText + afterSelection;
  };

  // Function to clean text from existing formatting tags
  const cleanTextFromTags = (text) => {
    return text
      .replace(/<bold>(.*?)<\/bold>/g, "$1")
      .replace(/<italic>(.*?)<\/italic>/g, "$1")
      .replace(/<underline>(.*?)<\/underline>/g, "$1")
      .replace(/<highlight>(.*?)<\/highlight>/g, "$1");
  };

  // Helper function to safely apply or remove a specific format
  const toggleFormat = (format, currentText) => {
    const hasFormat = new RegExp(`<${format}>`).test(currentText);

    if (hasFormat) {
      // Remove the specific format while preserving others
      // Use a more sophisticated approach that handles nested tags properly
      let result = currentText;

      // First, find all opening and closing tags of this format
      const openingTag = `<${format}>`;
      const closingTag = `</${format}>`;

      // Find the first opening tag
      const firstOpenIndex = result.indexOf(openingTag);
      if (firstOpenIndex !== -1) {
        // Find the corresponding closing tag
        let depth = 0;
        let closingIndex = -1;

        for (let i = firstOpenIndex; i < result.length; i++) {
          if (result.substring(i, i + openingTag.length) === openingTag) {
            depth++;
          } else if (
            result.substring(i, i + closingTag.length) === closingTag
          ) {
            depth--;
            if (depth === 0) {
              closingIndex = i;
              break;
            }
          }
        }

        if (closingIndex !== -1) {
          // Remove the outermost pair of tags
          const beforeTags = result.substring(0, firstOpenIndex);
          const insideTags = result.substring(
            firstOpenIndex + openingTag.length,
            closingIndex,
          );
          const afterTags = result.substring(closingIndex + closingTag.length);
          result = beforeTags + insideTags + afterTags;
        }
      }

      return result;
    } else {
      // Add the format - wrap the entire text
      // This preserves any existing formatting inside
      const result = `<${format}>${currentText}</${format}>`;

      return result;
    }
  };

  // Function to check if selected text or entire text has specific formatting
  const hasTextFormatting = (format) => {
    if (window.selectedTextInfo && window.selectedTextInfo.text.length > 0) {
      // Check if selected text has the formatting
      const selectedText = window.selectedTextInfo.text;
      const regex = new RegExp(`<${format}>(.*?)<\/${format}>`, "g");
      return regex.test(selectedText);
    } else {
      // Check if entire text has the formatting
      const textContent = selectedCircle.text || "";
      const regex = new RegExp(`<${format}>(.*?)<\/${format}>`, "g");
      return regex.test(textContent);
    }
  };

  // Get the current formatting states
  const isHighlighted = hasTextFormatting("highlight");
  const isBold = hasTextFormatting("bold");
  const isItalic = hasTextFormatting("italic");
  const isUnderlined = hasTextFormatting("underline");

  // Function to apply formatting while preserving existing formatting
  const applyFormatting = (text, format) => {
    // Check if the text contains this format anywhere
    const hasThisFormat = new RegExp(`<${format}>(.*?)<\/${format}>`, "g").test(
      text,
    );

    if (hasThisFormat) {
      // Remove this specific formatting while preserving others
      return text.replace(
        new RegExp(`<${format}>(.*?)<\/${format}>`, "g"),
        "$1",
      );
    } else {
      // Add this formatting around the text
      return `<${format}>${text}</${format}>`;
    }
  };

  const toggleBold = () => {
    // Use Lexical formatting if available
    if (window.applyLexicalBold) {
      window.applyLexicalBold();
      return;
    }

    // Fallback to existing text-based formatting
    if (window.selectedTextInfo && window.selectedTextInfo.text.length > 0) {
      const { start, end, element } = window.selectedTextInfo;

      // Get the current formatted text
      const currentFormattedText = selectedCircle.text || "";

      // Apply or remove bold formatting using the helper
      const newFormattedText = toggleFormat("bold", currentFormattedText);

      updateCircleProperties({ text: newFormattedText });

      // Update cursor position
      setTimeout(() => {
        element.focus();
        element.setSelectionRange(start, end);
      }, 0);
    } else {
      // Apply to entire text
      const newFontStyle = fontStyle.includes("bold")
        ? fontStyle.replace("bold", "").trim()
        : (fontStyle + " bold").trim();
      updateCircleProperties({ fontStyle: newFontStyle });
    }
  };

  const toggleItalic = () => {
    // Use Lexical formatting if available
    if (window.applyLexicalItalic) {
      window.applyLexicalItalic();
      return;
    }

    // Fallback to existing text-based formatting
    if (window.selectedTextInfo && window.selectedTextInfo.text.length > 0) {
      const { start, end, element } = window.selectedTextInfo;

      // Get the current formatted text
      const currentFormattedText = selectedCircle.text || "";

      // Apply or remove italic formatting using the helper
      const newFormattedText = toggleFormat("italic", currentFormattedText);

      updateCircleProperties({ text: newFormattedText });

      // Update cursor position
      setTimeout(() => {
        element.focus();
        element.setSelectionRange(start, end);
      }, 0);
    } else {
      // Apply to entire text
      const newFontStyle = fontStyle.includes("italic")
        ? fontStyle.replace("italic", "").trim()
        : (fontStyle + " italic").trim();
      updateCircleProperties({ fontStyle: newFontStyle });
    }
  };

  const toggleUnderline = () => {
    // Use Lexical formatting if available
    if (window.applyLexicalUnderline) {
      window.applyLexicalUnderline();
      return;
    }

    // Fallback to existing text-based formatting
    if (window.selectedTextInfo && window.selectedTextInfo.text.length > 0) {
      const { start, end, element } = window.selectedTextInfo;

      // Get the current formatted text
      const currentFormattedText = selectedCircle.text || "";

      // Apply or remove underline formatting using the helper
      const newFormattedText = toggleFormat("underline", currentFormattedText);

      updateCircleProperties({ text: newFormattedText });

      // Update cursor position
      setTimeout(() => {
        element.focus();
        element.setSelectionRange(start, end);
      }, 0);
    } else {
      // Apply to entire text
      const newTextDecoration = textDecoration.includes("underline")
        ? textDecoration.replace("underline", "").trim()
        : (textDecoration + " underline").trim();
      updateCircleProperties({ textDecoration: newTextDecoration });
    }
  };

  // Add highlight functionality
  const toggleHighlight = () => {
    // Use Lexical formatting if available
    if (window.applyLexicalHighlight) {
      window.applyLexicalHighlight();
      return;
    }

    // Fallback to existing text-based formatting
    if (window.selectedTextInfo && window.selectedTextInfo.text.length > 0) {
      const { start, end, element } = window.selectedTextInfo;

      // Get the current formatted text
      const currentFormattedText = selectedCircle.text || "";

      // Apply or remove highlight formatting using the helper
      const newFormattedText = toggleFormat("highlight", currentFormattedText);

      updateCircleProperties({ text: newFormattedText });

      // Update cursor position
      setTimeout(() => {
        element.focus();
        element.setSelectionRange(start, end);
      }, 0);
    } else {
      // Apply to entire text - use the same approach as other formats
      const currentFormattedText = selectedCircle.text || "";
      const newFormattedText = toggleFormat("highlight", currentFormattedText);

      updateCircleProperties({ text: newFormattedText });
    }
  };

  const savePresetsToStorage = (presets) => {
    localStorage.setItem("circleStylePresets", JSON.stringify(presets));
  };

  const saveDefaultPresetToStorage = (preset) => {
    localStorage.setItem(DEFAULT_PRESET_STORAGE_KEY, JSON.stringify(preset));
    localStorage.setItem(DEFAULT_PRESET_ID_STORAGE_KEY, String(preset.id));
    setDefaultPresetId(String(preset.id));
  };

  useEffect(() => {
    if (!Array.isArray(circlePresets) || circlePresets.length === 0) {
      const fallback = createDefaultCirclePreset();
      const next = [fallback];
      setCirclePresets(next);
      savePresetsToStorage(next);
      saveDefaultPresetToStorage(fallback);
      return;
    }
    const storedId = localStorage.getItem(DEFAULT_PRESET_ID_STORAGE_KEY);
    const hasStoredDefault = circlePresets.some(
      (preset) => String(preset.id) === String(storedId),
    );
    if (!storedId || !hasStoredDefault) {
      saveDefaultPresetToStorage(circlePresets[0]);
    }
  }, [circlePresets]);

  const saveCurrentStyleAsPreset = () => {
    if (editingPreset) return;
    const preset = {
      id: Date.now(),
      name: `Circle Preset ${circlePresets.length + 1}`,
      fill,
      fillOpacity,
      stroke: strokeColor,
      strokeOpacity: selectedCircle.strokeOpacity ?? 1,
      strokeWidth,
      fontSize,
      textColor,
      fontStyle,
      textDecoration,
      align,
      verticalAlign,
      text,
      rotation,
      radius,
      radiusX,
      radiusY,
      maintainAspectRatio,
    };
    const updated = [...circlePresets, preset];
    setCirclePresets(updated);

    // Immediately show the edit box for the newly created preset
    setEditingPreset(preset);
    setPendingDefaultPresetId(defaultPresetId);
    setDefaultPresetIdBeforeEdit(defaultPresetId);
    setPendingNewPresetId(preset.id);
  };

  const applyPreset = (preset) => {
    updateCircleProperties({
      fill: preset.fill,
      fillColor: preset.fill,
      fillOpacity: preset.fillOpacity,
      stroke: preset.stroke,
      strokeOpacity: preset.strokeOpacity,
      strokeWidth: preset.strokeWidth,
      fontSize: preset.fontSize,
      textColor: preset.textColor,
      fontStyle: preset.fontStyle,
      textDecoration: preset.textDecoration,
      align: preset.align,
      verticalAlign: preset.verticalAlign,
      text: preset.text,
      maintainAspectRatio: preset.maintainAspectRatio || false,
      isFirstTime: false,
    });
  };

  return (
    <div>
      {!showTextEditor && (
        <>
          {/* Position */}
          <div
            style={{
              background: "#f5f5f5",
              borderRadius: "8px",
              padding: "10px 12px",
              marginBottom: "12px",
              display: "flex",
              gap: "16px",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <label style={{ whiteSpace: "nowrap" }}>XPos</label>
              <input
                type="number"
                value={getDisplayValue(localInputValues.x)}
                min={MIN_X_CONSTRAINED}
                max={MAX_X_CONSTRAINED}
                onKeyDown={handleEnterKey}
                onChange={(e) => {
                  const { value } = e.target;
                  const inputType = e.nativeEvent?.inputType;
                  setLocalInputValues((prev) => ({
                    ...prev,
                    x: value,
                  }));
                  if (value === "" || shouldDeferInputUpdate(inputType)) {
                    return;
                  }
                  const clamped = clampNumericValue(
                    value,
                    MIN_X_CONSTRAINED,
                    MAX_X_CONSTRAINED,
                  );
                  if (clamped === null) {
                    return;
                  }
                  // CRITICAL FIX: Ensure clamped value respects constraints
                  const finalValue = Math.max(
                    MIN_X_CONSTRAINED,
                    Math.min(MAX_X_CONSTRAINED, clamped),
                  );
                  updateCircleProperties({ x: finalValue });
                  setLocalInputValues((prev) => ({
                    ...prev,
                    x: finalValue,
                  }));
                }}
                onBlur={(e) => {
                  const { value } = e.target;
                  const fallback = value === "" ? MIN_X_CONSTRAINED : value;
                  const clamped = clampNumericValue(
                    fallback,
                    MIN_X_CONSTRAINED,
                    MAX_X_CONSTRAINED,
                  );
                  if (clamped === null) {
                    return;
                  }
                  // CRITICAL FIX: Ensure clamped value respects constraints
                  const finalValue = Math.max(
                    MIN_X_CONSTRAINED,
                    Math.min(MAX_X_CONSTRAINED, clamped),
                  );
                  updateCircleProperties({ x: finalValue });
                  setLocalInputValues((prev) => ({
                    ...prev,
                    x: finalValue,
                  }));
                }}
                style={{
                  width: "60px",
                  padding: "4px 8px",
                  marginLeft: "10px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  background: "#fff",
                }}
              />
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <label style={{ whiteSpace: "nowrap" }}>YPos</label>
              <div
                className="circle-editor-number-with-spinner"
                style={{ marginLeft: "10px" }}
              >
                <input
                  type="number"
                  className="circle-editor-pos-with-buttons"
                  value={getDisplayValue(localInputValues.y)}
                  min={MIN_Y_CONSTRAINED}
                  max={MAX_Y_CONSTRAINED}
                  onKeyDown={handleEnterKey}
                  onChange={(e) => {
                    const { value } = e.target;
                    const inputType = e.nativeEvent?.inputType;
                    setLocalInputValues((prev) => ({
                      ...prev,
                      y: value,
                    }));
                    if (value === "" || shouldDeferInputUpdate(inputType)) {
                      return;
                    }
                    const clamped = clampNumericValue(
                      value,
                      MIN_Y_CONSTRAINED,
                      MAX_Y_CONSTRAINED,
                    );
                    if (clamped === null) {
                      return;
                    }
                    const finalValue = Math.max(
                      MIN_Y_CONSTRAINED,
                      Math.min(MAX_Y_CONSTRAINED, clamped),
                    );
                    updateCircleProperties({ y: finalValue });
                    setLocalInputValues((prev) => ({
                      ...prev,
                      y: finalValue,
                    }));
                  }}
                  onBlur={(e) => {
                    const { value } = e.target;
                    const fallback = value === "" ? MIN_Y_CONSTRAINED : value;
                    const clamped = clampNumericValue(
                      fallback,
                      MIN_Y_CONSTRAINED,
                      MAX_Y_CONSTRAINED,
                    );
                    if (clamped === null) {
                      return;
                    }
                    const finalValue = Math.max(
                      MIN_Y_CONSTRAINED,
                      Math.min(MAX_Y_CONSTRAINED, clamped),
                    );
                    updateCircleProperties({ y: finalValue });
                    setLocalInputValues((prev) => ({
                      ...prev,
                      y: finalValue,
                    }));
                  }}
                />
                <div className="circle-editor-spinner-buttons">
                  <button
                    type="button"
                    title="Increase Y (hold to repeat)"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      startYStep(1);
                    }}
                    onPointerDown={(e) => {
                      e.preventDefault();
                      startYStep(1);
                    }}
                    onMouseUp={stopYStep}
                    onPointerUp={stopYStep}
                    onMouseLeave={stopYStep}
                    onPointerLeave={stopYStep}
                    onBlur={stopYStep}
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    title="Decrease Y (hold to repeat)"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      startYStep(-1);
                    }}
                    onPointerDown={(e) => {
                      e.preventDefault();
                      startYStep(-1);
                    }}
                    onMouseUp={stopYStep}
                    onPointerUp={stopYStep}
                    onMouseLeave={stopYStep}
                    onPointerLeave={stopYStep}
                    onBlur={stopYStep}
                  >
                    ▼
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Radius - only show when maintainAspectRatio is true */}
          {maintainAspectRatio && (
            <div
              style={{
                background: "#f5f5f5",
                borderRadius: "8px",
                padding: "10px 12px",
                marginBottom: "12px",
                display: "flex",
                gap: "16px",
                alignItems: "center",
              }}
            >
              <label style={{ whiteSpace: "nowrap" }}>Radius:</label>
              <input
                type="number"
                value={localInputValues.radius}
                min={MIN_RADIUS}
                max={maxRadiusByStageFit}
                onKeyDown={handleEnterKey}
                onChange={(e) => {
                  const { value } = e.target;
                  const inputType = e.nativeEvent?.inputType;
                  setLocalInputValues((prev) => ({
                    ...prev,
                    radius: value,
                  }));
                  if (value === "" || shouldDeferInputUpdate(inputType)) {
                    return;
                  }
                  const clamped = clampNumericValue(
                    value,
                    MIN_RADIUS,
                    maxRadiusByStageFit,
                  );
                  if (clamped === null) {
                    return;
                  }
                  handleRadiusXChange(clamped);
                  setLocalInputValues((prev) => ({
                    ...prev,
                    radius: clamped,
                    radiusX: clamped,
                    radiusY: clamped,
                  }));
                }}
                onBlur={(e) => {
                  const { value } = e.target;
                  const fallback = value === "" ? MIN_RADIUS : value;
                  const clamped = clampNumericValue(
                    fallback,
                    MIN_RADIUS,
                    maxRadiusByStageFit,
                  );
                  if (clamped === null) {
                    return;
                  }
                  handleRadiusXChange(clamped);
                  setLocalInputValues((prev) => ({
                    ...prev,
                    radius: clamped,
                    radiusX: clamped,
                    radiusY: clamped,
                  }));
                }}
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid #ccc",
                }}
              />
            </div>
          )}

          {/* RadiusX and RadiusY - only show when maintainAspectRatio is false */}
          {!maintainAspectRatio && (
            <>
              <div
                style={{
                  background: "#f5f5f5",
                  borderRadius: "8px",
                  padding: "10px 12px",
                  marginBottom: "12px",
                  display: "flex",
                  gap: "16px",
                  alignItems: "center",
                }}
              >
                <label style={{ whiteSpace: "nowrap" }}>Radius X:</label>
                <input
                  type="number"
                  value={localInputValues.radiusX}
                  min={MIN_RADIUS_X}
                  max={maxRadiusXByStageFit}
                  onKeyDown={handleEnterKey}
                  onChange={(e) => {
                    const { value } = e.target;
                    const inputType = e.nativeEvent?.inputType;
                    setLocalInputValues((prev) => ({
                      ...prev,
                      radiusX: value,
                    }));
                    if (value === "" || shouldDeferInputUpdate(inputType)) {
                      return;
                    }
                    const clamped = clampNumericValue(
                      value,
                      MIN_RADIUS_X,
                      maxRadiusXByStageFit,
                    );
                    if (clamped === null) {
                      return;
                    }
                    handleRadiusXChange(clamped);
                    setLocalInputValues((prev) => ({
                      ...prev,
                      radiusX: clamped,
                    }));
                  }}
                  onBlur={(e) => {
                    const { value } = e.target;
                    const fallback = value === "" ? MIN_RADIUS_X : value;
                    const clamped = clampNumericValue(
                      fallback,
                      MIN_RADIUS_X,
                      maxRadiusXByStageFit,
                    );
                    if (clamped === null) {
                      return;
                    }
                    handleRadiusXChange(clamped);
                    setLocalInputValues((prev) => ({
                      ...prev,
                      radiusX: clamped,
                    }));
                  }}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ccc",
                  }}
                />
              </div>

              <div
                style={{
                  background: "#f5f5f5",
                  borderRadius: "8px",
                  padding: "10px 12px",
                  marginBottom: "12px",
                  display: "flex",
                  gap: "16px",
                  alignItems: "center",
                }}
              >
                <label style={{ whiteSpace: "nowrap" }}>Radius Y:</label>
                <input
                  type="number"
                  value={localInputValues.radiusY}
                  min={MIN_RADIUS_Y}
                  max={maxRadiusYByStageFit}
                  onKeyDown={handleEnterKey}
                  onChange={(e) => {
                    const { value } = e.target;
                    const inputType = e.nativeEvent?.inputType;
                    setLocalInputValues((prev) => ({
                      ...prev,
                      radiusY: value,
                    }));
                    if (value === "" || shouldDeferInputUpdate(inputType)) {
                      return;
                    }
                    const clamped = clampNumericValue(
                      value,
                      MIN_RADIUS_Y,
                      maxRadiusYByStageFit,
                    );
                    if (clamped === null) {
                      return;
                    }
                    handleRadiusYChange(clamped);
                    setLocalInputValues((prev) => ({
                      ...prev,
                      radiusY: clamped,
                    }));
                  }}
                  onBlur={(e) => {
                    const { value } = e.target;
                    const fallback = value === "" ? MIN_RADIUS_Y : value;
                    const clamped = clampNumericValue(
                      fallback,
                      MIN_RADIUS_Y,
                      maxRadiusYByStageFit,
                    );
                    if (clamped === null) {
                      return;
                    }
                    handleRadiusYChange(clamped);
                    setLocalInputValues((prev) => ({
                      ...prev,
                      radiusY: clamped,
                    }));
                  }}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ccc",
                  }}
                />
              </div>
            </>
          )}

          {/* Duplicate to Selected Location button */}
          {onToggleCloneMode && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                // Use setTimeout to ensure the toggle happens after event propagation is stopped
                setTimeout(() => {
                  onToggleCloneMode();
                }, 0);
              }}
              style={{
                width: "100%",
                padding: "10px 20px",
                backgroundColor: isCloneMode ? "#3B82F6" : "#F3F4F6",
                color: isCloneMode ? "white" : "#374151",
                border: isCloneMode ? "1px solid #3B82F6" : "1px solid #D1D5DB",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: 500,
                cursor: "pointer",
                transition: "all 0.2s ease",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "4px",
                marginBottom: "12px",
              }}
            >
              <CopyIcon
                width={16}
                height={19}
                fill={isCloneMode ? "white" : "#374151"}
              />
              <span>Duplicate to Selected Location</span>
            </button>
          )}

          {/* Aspect Ratio Checkbox */}
          <div
            style={{
              background: "#f5f5f5",
              borderRadius: "8px",
              padding: "10px 12px",
              marginBottom: "12px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <input
              type="checkbox"
              id="maintainAspectRatio"
              checked={maintainAspectRatio}
              onChange={(e) => handleAspectRatioChange(e.target.checked)}
              style={{
                width: "16px",
                height: "16px",
                cursor: "pointer",
              }}
            />
            <label
              htmlFor="maintainAspectRatio"
              style={{
                whiteSpace: "nowrap",
                fontSize: "14px",
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              Maintain Proportions (Circle)
            </label>
          </div>

          <div
            style={{
              background: "#f5f5f5",
              borderRadius: "8px",
              padding: "10px 12px",
              marginBottom: "12px",
              display: "flex",
              gap: "16px",
              alignItems: "center",
            }}
          >
            <label style={{ whiteSpace: "nowrap" }}>Rotation</label>
            <div style={{ display: "flex", alignItems: "center" }}>
              <input
                type="text"
                value={
                  isEditingRotation
                    ? localInputValues.rotation
                    : localInputValues.rotation !== undefined &&
                        localInputValues.rotation !== ""
                      ? `${localInputValues.rotation}°`
                      : "0°"
                }
                onKeyDown={handleEnterKey}
                onChange={(e) => {
                  const value = e.target.value;
                  // Remove degree symbol and only allow numbers
                  let numericValue = value.replace(/°/g, "");
                  // Trim leading zeros (but keep one zero if the value is just zeros)
                  if (numericValue.length > 1 && /^0+/.test(numericValue)) {
                    numericValue = numericValue.replace(/^0+/, "");
                  }
                  if (numericValue === "" || /^\d+$/.test(numericValue)) {
                    setLocalInputValues((prev) => ({
                      ...prev,
                      rotation: numericValue,
                    }));
                  }
                }}
                onFocus={() => {
                  setIsEditingRotation(true);
                }}
                onBlur={(e) => {
                  setIsEditingRotation(false);
                  let val = e.target.value.replace(/°/g, "");
                  let num = parseInt(val, 10);
                  if (val === "" || isNaN(num)) {
                    applyRotationWithEllipseClamp(0);
                    return;
                  }
                  applyRotationWithEllipseClamp(num);
                }}
                style={{
                  width: "80px",
                  padding: "6px 8px",
                  border: "1px solid #ccc",
                  borderRight: "none",
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                  fontSize: "14px",
                }}
              />
              <div style={{ display: "flex", flexDirection: "column" }}>
                <button
                  onMouseDown={() => startRotationChange(1)}
                  onMouseUp={stopRotationChange}
                  onMouseLeave={stopRotationChange}
                  onBlur={stopRotationChange}
                  style={{
                    width: "20px",
                    height: "15px",
                    border: "1px solid #ccc",
                    borderTopRightRadius: "4px",
                    borderBottom: "none",
                    backgroundColor: "#f8f9fa",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "10px",
                    color: "#6C757D",
                  }}
                  title="Hold to increase rotation"
                >
                  ▲
                </button>
                <button
                  onMouseDown={() => startRotationChange(-1)}
                  onMouseUp={stopRotationChange}
                  onMouseLeave={stopRotationChange}
                  onBlur={stopRotationChange}
                  style={{
                    width: "20px",
                    height: "15px",
                    border: "1px solid #ccc",
                    borderBottomRightRadius: "4px",
                    backgroundColor: "#f8f9fa",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "10px",
                    color: "#6C757D",
                  }}
                  title="Hold to decrease rotation"
                >
                  ▼
                </button>
              </div>
            </div>
          </div>

          {/* Style Section */}
          <div
            style={{
              background: "#f7f7f7",
              borderRadius: "8px",
              padding: "16px",
              marginBottom: "12px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
                cursor: "pointer",
              }}
              onClick={() => setIsStyleSectionExpanded(!isStyleSectionExpanded)}
            >
              <h4
                style={{ margin: "0", fontWeight: "normal", fontSize: "14px" }}
              >
                Style
              </h4>
              <svg
                width="12"
                height="7"
                viewBox="0 0 14 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  transform: isStyleSectionExpanded
                    ? "rotate(0deg)"
                    : "rotate(180deg)",
                  transition: "transform 0.2s",
                }}
              >
                <path
                  d="M1.61906 7.75961L0.734375 6.87492L7.36906 0.240234L14.0038 6.87492L13.1191 7.75961L7.36906 2.0168L1.61906 7.75961Z"
                  fill="#6C757D"
                />
              </svg>
            </div>

            {isStyleSectionExpanded && (
              <>
                {/* Fill Color */}
                <div
                  style={{
                    marginBottom: "12px",
                    display: "flex",
                    gap: "16px",
                    alignItems: "center",
                  }}
                >
                  <label style={{ whiteSpace: "nowrap", fontSize: "13px" }}>
                    Fill Color
                  </label>
                  <input
                    type="color"
                    value={rgbaToHex(fill)}
                    onChange={(e) =>
                      updateCircleProperties({
                        fill: e.target.value,
                        fillColor: e.target.value,
                      })
                    }
                    style={{
                      width: "100%",
                      padding: "4px",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                    }}
                  />
                </div>

                {/* Fill Transparency */}
                <div
                  style={{
                    marginBottom: "12px",
                    display: "flex",
                    gap: "16px",
                    alignItems: "center",
                  }}
                >
                  <label style={{ whiteSpace: "nowrap", fontSize: "13px" }}>
                    Transparency
                  </label>
                  <div style={{ display: "flex", flex: 1 }}>
                    <input
                      type="number"
                      step="1"
                      min="0"
                      max="100"
                      value={localInputValues.fillOpacity}
                      onKeyDown={handleEnterKey}
                      onChange={(e) => {
                        const { value } = e.target;
                        const inputType = e.nativeEvent?.inputType;
                        setLocalInputValues((prev) => ({
                          ...prev,
                          fillOpacity: value,
                        }));
                        if (value === "" || shouldDeferInputUpdate(inputType)) {
                          return;
                        }
                        const clamped = clampNumericValue(value, 0, 100);
                        if (clamped === null) {
                          return;
                        }
                        updateCircleProperties({ fillOpacity: clamped / 100 });
                        setLocalInputValues((prev) => ({
                          ...prev,
                          fillOpacity: clamped,
                        }));
                      }}
                      onBlur={(e) => {
                        const { value } = e.target;
                        const fallback = value === "" ? 0 : value;
                        const clamped = clampNumericValue(fallback, 0, 100);
                        if (clamped === null) {
                          updateCircleProperties({ fillOpacity: 0 });
                          setLocalInputValues((prev) => ({
                            ...prev,
                            fillOpacity: 0,
                          }));
                          return;
                        }
                        updateCircleProperties({ fillOpacity: clamped / 100 });
                        setLocalInputValues((prev) => ({
                          ...prev,
                          fillOpacity: clamped,
                        }));
                      }}
                      style={{
                        flex: 1,
                        padding: "4px",
                        border: "1px solid #ccc",
                        borderRight: "none",
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                      }}
                    />

                    <span
                      style={{
                        padding: "4px 8px",
                        border: "1px solid #ccc",
                        borderLeft: "none",
                        backgroundColor: "#f2f2f2",
                        borderTopRightRadius: "4px",
                        borderBottomRightRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      %
                    </span>
                  </div>
                </div>

                {/* Stroke Color */}
                <div
                  style={{
                    marginBottom: "12px",
                    display: "flex",
                    gap: "16px",
                    alignItems: "center",
                  }}
                >
                  <label style={{ whiteSpace: "nowrap", fontSize: "13px" }}>
                    Color
                  </label>
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <button
                      onClick={() => {
                        const newColor = "#dc3545";
                        updateCircleProperties({ stroke: newColor });
                        setLocalInputValues((prev) => ({
                          ...prev,
                          strokeColorHex: newColor
                            .toUpperCase()
                            .replace("#", ""),
                        }));
                      }}
                      style={{
                        width: "24px",
                        height: "24px",
                        backgroundColor: "#dc3545",
                        border: "1px solid #ccc",
                        borderRadius: "2px",
                        cursor: "pointer",
                      }}
                    />
                    <button
                      onClick={() => {
                        const newColor = "#fd7e14";
                        updateCircleProperties({ stroke: newColor });
                        setLocalInputValues((prev) => ({
                          ...prev,
                          strokeColorHex: newColor
                            .toUpperCase()
                            .replace("#", ""),
                        }));
                      }}
                      style={{
                        width: "24px",
                        height: "24px",
                        backgroundColor: "#fd7e14",
                        border: "1px solid #ccc",
                        borderRadius: "2px",
                        cursor: "pointer",
                      }}
                    />
                    <input
                      type="color"
                      value={rgbaToHex(strokeColor)}
                      onChange={(e) => {
                        const newColor = e.target.value;
                        updateCircleProperties({ stroke: newColor });
                        setLocalInputValues((prev) => ({
                          ...prev,
                          strokeColorHex: newColor
                            .toUpperCase()
                            .replace("#", ""),
                        }));
                      }}
                      style={{
                        width: "40px",
                        height: "24px",
                        border: "1px solid #ccc",
                        borderRadius: "2px",
                        cursor: "pointer",
                        padding: 0,
                      }}
                    />
                    <input
                      type="text"
                      value={`${localInputValues.strokeColorHex}`}
                      maxLength={10}
                      onKeyDown={handleEnterKey}
                      onChange={(e) => {
                        // Strip "Hex " prefix if present, then process hex value
                        let inputValue = e.target.value
                          .replace(/^Hex\s*/i, "")
                          .toUpperCase()
                          .replace("#", "");
                        // Limit to max 6 hex characters
                        if (inputValue.length > 6) {
                          inputValue = inputValue.slice(0, 6);
                        }
                        setLocalInputValues((prev) => ({
                          ...prev,
                          strokeColorHex: inputValue,
                        }));
                      }}
                      onBlur={(e) => {
                        // Strip "Hex " prefix before validating
                        const hexValue = e.target.value.replace(/^Hex\s*/i, "");
                        const validColor = validateAndParseHexColor(hexValue);

                        if (validColor) {
                          // Valid hex code - update the stroke color
                          updateCircleProperties({ stroke: validColor });
                        } else {
                          // Invalid hex code - revert to current color
                          const currentHex = rgbaToHex(strokeColor)
                            .toUpperCase()
                            .replace("#", "");
                          setLocalInputValues((prev) => ({
                            ...prev,
                            strokeColorHex: currentHex,
                          }));
                        }
                      }}
                      placeholder="Hex 000000"
                      style={{
                        flex: 1,
                        padding: "4px 8px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        backgroundColor: "white",
                        fontSize: "12px",
                      }}
                    />
                  </div>
                </div>

                {/* Stroke Width */}
                <div
                  style={{
                    marginBottom: "12px",
                    display: "flex",
                    gap: "16px",
                    alignItems: "center",
                  }}
                >
                  <label style={{ whiteSpace: "nowrap", fontSize: "13px" }}>
                    Stroke Size
                  </label>
                  <input
                    type="number"
                    min={MIN_STROKE_WIDTH}
                    max={MAX_STROKE_WIDTH}
                    value={localInputValues.strokeWidth}
                    onKeyDown={handleEnterKey}
                    onChange={(e) => {
                      const { value } = e.target;
                      const inputType = e.nativeEvent?.inputType;
                      setLocalInputValues((prev) => ({
                        ...prev,
                        strokeWidth: value,
                      }));
                      if (value === "" || shouldDeferInputUpdate(inputType)) {
                        return;
                      }
                      const clamped = clampNumericValue(
                        value,
                        MIN_STROKE_WIDTH,
                        MAX_STROKE_WIDTH,
                      );
                      if (clamped === null) {
                        return;
                      }
                      updateCircleProperties({ strokeWidth: clamped });
                      setLocalInputValues((prev) => ({
                        ...prev,
                        strokeWidth: clamped,
                      }));
                    }}
                    onBlur={(e) => {
                      const { value } = e.target;
                      const fallback = value === "" ? MIN_STROKE_WIDTH : value;
                      const clamped = clampNumericValue(
                        fallback,
                        MIN_STROKE_WIDTH,
                        MAX_STROKE_WIDTH,
                      );
                      if (clamped === null) {
                        return;
                      }
                      updateCircleProperties({ strokeWidth: clamped });
                      setLocalInputValues((prev) => ({
                        ...prev,
                        strokeWidth: clamped,
                      }));
                    }}
                    style={{
                      width: "100%",
                      padding: "4px",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                    }}
                  />
                </div>

                {/* Stroke Transparency */}
                <div
                  style={{
                    marginBottom: "12px",
                    display: "flex",
                    gap: "16px",
                    alignItems: "center",
                  }}
                >
                  <label style={{ whiteSpace: "nowrap", fontSize: "13px" }}>
                    Transparency
                  </label>
                  <div style={{ display: "flex", flex: 1 }}>
                    <input
                      type="number"
                      step="1"
                      min="0"
                      max="100"
                      value={localInputValues.strokeOpacity}
                      onKeyDown={handleEnterKey}
                      onChange={(e) => {
                        const { value } = e.target;
                        const inputType = e.nativeEvent?.inputType;
                        setLocalInputValues((prev) => ({
                          ...prev,
                          strokeOpacity: value,
                        }));
                        if (value === "" || shouldDeferInputUpdate(inputType)) {
                          return;
                        }
                        const clamped = clampNumericValue(value, 0, 100);
                        if (clamped === null) {
                          return;
                        }
                        updateCircleProperties({
                          strokeOpacity: clamped / 100,
                        });
                        setLocalInputValues((prev) => ({
                          ...prev,
                          strokeOpacity: clamped,
                        }));
                      }}
                      onBlur={(e) => {
                        const { value } = e.target;
                        const fallback = value === "" ? 0 : value;
                        const clamped = clampNumericValue(fallback, 0, 100);
                        if (clamped === null) {
                          updateCircleProperties({ strokeOpacity: 0 });
                          setLocalInputValues((prev) => ({
                            ...prev,
                            strokeOpacity: 0,
                          }));
                          return;
                        }
                        updateCircleProperties({
                          strokeOpacity: clamped / 100,
                        });
                        setLocalInputValues((prev) => ({
                          ...prev,
                          strokeOpacity: clamped,
                        }));
                      }}
                      style={{
                        flex: 1,
                        padding: "4px",
                        border: "1px solid #ccc",
                        borderRight: "none",
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                      }}
                    />
                    <span
                      style={{
                        padding: "4px 8px",
                        border: "1px solid #ccc",
                        borderLeft: "none",
                        backgroundColor: "#f2f2f2",
                        borderTopRightRadius: "4px",
                        borderBottomRightRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      %
                    </span>
                  </div>
                </div>
                <button
                  onClick={saveCurrentStyleAsPreset}
                  disabled={Boolean(editingPreset)}
                  style={{
                    background: "#f7f7f7",
                    border: "none",
                    width: "100%",
                    color: "#007bff",
                    fontSize: "13px",
                    opacity: editingPreset ? 0.5 : 1,
                    cursor: editingPreset ? "not-allowed" : "pointer",
                  }}
                >
                  <span
                    style={{
                      fontSize: "16px",
                      marginRight: "8px",
                    }}
                  >
                    +
                  </span>
                  Add this style To Preset
                </button>
              </>
            )}
          </div>

          {/* Rotation */}

          {/* Add to Preset Button */}

          {/* Preset Controls */}
          {/* Preset Controls */}
          <div
            style={{
              background: "#f7f7f7",
              borderRadius: "5px",
              marginTop: "-10px",
              padding: "1rem",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
              }}
              onClick={() => setIsStylesExpanded(!isStylesExpanded)}
            >
              <h4 style={{ margin: "0", fontWeight: "normal" }}>Styles</h4>
              <svg
                width="12"
                height="7"
                viewBox="0 0 14 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  transform: isStylesExpanded
                    ? "rotate(0deg)"
                    : "rotate(180deg)",
                  transition: "transform 0.2s",
                }}
              >
                <path
                  d="M1.61906 7.75961L0.734375 6.87492L7.36906 0.240234L14.0038 6.87492L13.1191 7.75961L7.36906 2.0168L1.61906 7.75961Z"
                  fill="#6C757D"
                />
              </svg>
            </div>

            {isStylesExpanded && (
              <>
                {/* List Presets */}
                {circlePresets.map((preset) => (
                  <div key={preset.id}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "10px 0",
                        borderBottom: "1px solid #eee",
                      }}
                    >
                    <button
                      type="button"
                      onClick={() => {
                        if (editingPreset && editingPreset.id !== preset.id)
                          return;
                        applyPreset(preset);
                      }}
                      disabled={
                        Boolean(editingPreset) &&
                        editingPreset.id !== preset.id
                      }
                      style={{
                        background: "none",
                        border: "none",
                        cursor:
                          editingPreset && editingPreset.id !== preset.id
                            ? "not-allowed"
                            : "pointer",
                        textAlign: "left",
                        flexGrow: 1,
                        padding: 0,
                        fontSize: "1rem",
                        color:
                          editingPreset && editingPreset.id !== preset.id
                            ? "#9ca3af"
                            : "inherit",
                        opacity:
                          editingPreset && editingPreset.id !== preset.id
                            ? 0.55
                            : 1,
                      }}
                    >
                      {preset.name}
                    </button>
                    <div style={{ display: "flex", gap: "10px" }}>
                      <button
                        type="button"
                        disabled
                        title="Default is set in edit panel"
                        style={{
                          backgroundColor:
                            String(preset.id) === String(defaultPresetId)
                              ? "#2563eb"
                              : "#e5e7eb",
                          color:
                            String(preset.id) === String(defaultPresetId)
                              ? "white"
                              : "#111827",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "not-allowed",
                          padding: "2px 6px",
                          fontSize: "11px",
                          opacity: 0.45,
                        }}
                      >
                        Default
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (editingPreset) return;
                          setEditingPreset(preset);
                          setPendingDefaultPresetId(defaultPresetId);
                          setDefaultPresetIdBeforeEdit(defaultPresetId);
                        }}
                        disabled={Boolean(editingPreset)}
                        title="Edit"
                        style={{
                          backgroundColor: "#007bff",
                          border: "none",
                          borderRadius: "4px",
                          cursor: editingPreset ? "not-allowed" : "pointer",
                          padding: "2px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "22px",
                          height: "22px",
                          opacity: editingPreset ? 0.45 : 1,
                        }}
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <g clip-path="url(#clip0_15909_16836)">
                            <path
                              d="M11.6263 1.45481C11.6964 1.5251 11.7357 1.6203 11.7357 1.71956C11.7357 1.81881 11.6964 1.91401 11.6263 1.98431L10.844 2.76731L9.34404 1.26731L10.1263 0.484307C10.1966 0.414005 10.292 0.374512 10.3914 0.374512C10.4908 0.374512 10.5862 0.414005 10.6565 0.484307L11.6263 1.45406V1.45481ZM10.3138 3.29681L8.81379 1.79681L3.70404 6.90731C3.66276 6.94858 3.63168 6.99891 3.61329 7.05431L3.00954 8.86481C2.99859 8.89781 2.99703 8.93321 3.00505 8.96705C3.01306 9.00089 3.03033 9.03184 3.05492 9.05643C3.07951 9.08102 3.11045 9.09828 3.14429 9.1063C3.17813 9.11431 3.21353 9.11276 3.24654 9.10181L5.05704 8.49806C5.11236 8.47988 5.16269 8.44906 5.20404 8.40806L10.3138 3.29681Z"
                              fill="white"
                            />
                            <path
                              fill-rule="evenodd"
                              clip-rule="evenodd"
                              d="M0.75 10.125C0.75 10.4234 0.868526 10.7095 1.0795 10.9205C1.29048 11.1315 1.57663 11.25 1.875 11.25H10.125C10.4234 11.25 10.7095 11.1315 10.9205 10.9205C11.1315 10.7095 11.25 10.4234 11.25 10.125V5.625C11.25 5.52554 11.2105 5.43016 11.1402 5.35984C11.0698 5.28951 10.9745 5.25 10.875 5.25C10.7755 5.25 10.6802 5.28951 10.6098 5.35984C10.5395 5.43016 10.5 5.52554 10.5 5.625V10.125C10.5 10.2245 10.4605 10.3198 10.3902 10.3902C10.3198 10.4605 10.2245 10.5 10.125 10.5H1.875C1.77554 10.5 1.68016 10.4605 1.60984 10.3902C1.53951 10.3198 1.5 10.2245 1.5 10.125V1.875C1.5 1.77554 1.53951 1.68016 1.60984 1.60984C1.68016 1.53951 1.77554 1.5 1.875 1.5H6.75C6.84946 1.5 6.94484 1.46049 7.01517 1.39017C7.08549 1.31984 7.125 1.22446 7.125 1.125C7.125 1.02554 7.08549 0.930161 7.01517 0.859835C6.94484 0.789509 6.84946 0.75 6.75 0.75H1.875C1.57663 0.75 1.29048 0.868526 1.0795 1.0795C0.868526 1.29048 0.75 1.57663 0.75 1.875V10.125Z"
                              fill="white"
                            />
                          </g>
                          <defs>
                            <clipPath id="clip0_15909_16836">
                              <rect width="12" height="12" fill="white" />
                            </clipPath>
                          </defs>
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (editingPreset || circlePresets.length <= 1) return;
                          const filtered = circlePresets.filter(
                            (p) => p.id !== preset.id,
                          );
                          setCirclePresets(filtered);
                          savePresetsToStorage(filtered);
                          const storedDefaultId = localStorage.getItem(
                            DEFAULT_PRESET_ID_STORAGE_KEY,
                          );
                          if (
                            String(defaultPresetId) === String(preset.id) ||
                            (storedDefaultId != null &&
                              String(storedDefaultId) === String(preset.id))
                          ) {
                            saveDefaultPresetToStorage(filtered[0]);
                          }
                          // Clear editing state if the deleted preset is being edited
                          if (editingPreset && editingPreset.id === preset.id) {
                            setEditingPreset(null);
                            setPendingDefaultPresetId(null);
                            setDefaultPresetIdBeforeEdit(null);
                          }
                        }}
                        disabled={Boolean(editingPreset) || circlePresets.length <= 1}
                        title="Delete"
                        style={{
                          backgroundColor: "#dc3545",
                          border: "none",
                          borderRadius: "4px",
                          cursor:
                            editingPreset || circlePresets.length <= 1
                              ? "not-allowed"
                              : "pointer",
                          padding: "2px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "22px",
                          height: "22px",
                          opacity:
                            editingPreset || circlePresets.length <= 1 ? 0.45 : 1,
                        }}
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 18 18"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect width="18" height="18" rx="4" fill="#DC3545" />
                          <path
                            d="M5.875 14.5C5.53125 14.5 5.23698 14.3803 4.99219 14.141C4.7474 13.9016 4.625 13.6139 4.625 13.2778V5.33333H4V4.11111H7.125V3.5H10.875V4.11111H14V5.33333H13.375V13.2778C13.375 13.6139 13.2526 13.9016 13.0078 14.141C12.763 14.3803 12.4688 14.5 12.125 14.5H5.875ZM7.125 12.0556H8.375V6.55556H7.125V12.0556ZM9.625 12.0556H10.875V6.55556H9.625V12.0556Z"
                            fill="white"
                          />
                        </svg>
                      </button>
                    </div>
                    </div>
                    {editingPreset && editingPreset.id === preset.id && (
                      <div
                        style={{
                          marginTop: "8px",
                          marginBottom: "12px",
                          padding: "12px",
                          border: "1px solid #ccc",
                          borderRadius: "6px",
                        }}
                      >
                        <h4>Edit Preset: {editingPreset.name}</h4>
                        <input
                          type="text"
                          value={editingPreset.name}
                          onChange={(e) =>
                            setEditingPreset({
                              ...editingPreset,
                              name: e.target.value,
                            })
                          }
                          placeholder="Preset name"
                          style={{
                            width: "100%",
                            padding: "6px",
                            border: "1px solid #ccc",
                            marginBottom: "12px",
                          }}
                        />
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: "12px",
                            fontSize: "13px",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={
                              String(editingPreset.id) ===
                              String(pendingDefaultPresetId ?? defaultPresetId)
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                setPendingDefaultPresetId(
                                  String(editingPreset.id),
                                );
                              }
                            }}
                          />
                          Set as default preset
                        </label>
                        <button
                          onClick={() => {
                            const trimmedName = editingPreset.name.trim();
                            const isDuplicate = circlePresets.some(
                              (p) =>
                                p.id !== editingPreset.id &&
                                p.name.trim().toLowerCase() ===
                                  trimmedName.toLowerCase(),
                            );
                            if (isDuplicate) {
                              alert(
                                "A preset with this name already exists. Please choose a different name.",
                              );
                              return;
                            }
                            const updated = circlePresets.map((p) =>
                              p.id === editingPreset.id
                                ? { ...editingPreset, name: trimmedName }
                                : p,
                            );
                            const nextDefaultPresetId =
                              pendingDefaultPresetId ?? defaultPresetId;
                            const nextDefaultPreset = updated.find(
                              (p) =>
                                String(p.id) === String(nextDefaultPresetId),
                            );
                            setCirclePresets(updated);
                            savePresetsToStorage(updated);
                            if (nextDefaultPreset) {
                              saveDefaultPresetToStorage(nextDefaultPreset);
                            }
                            setEditingPreset(null);
                            setPendingDefaultPresetId(null);
                            setDefaultPresetIdBeforeEdit(null);
                            setPendingNewPresetId(null);
                          }}
                          style={{
                            backgroundColor: "#2563eb",
                            color: "white",
                            padding: "6px 12px",
                            borderRadius: "4px",
                            border: "none",
                            cursor: "pointer",
                            marginRight: "8px",
                          }}
                        >
                          Save Changes
                        </button>
                        <button
                          onClick={() => {
                            const rolledBack = pendingNewPresetId
                              ? circlePresets.filter(
                                  (p) => p.id !== pendingNewPresetId,
                                )
                              : circlePresets;
                            if (pendingNewPresetId) {
                              setCirclePresets(rolledBack);
                            }
                            const fallbackDefaultId =
                              defaultPresetIdBeforeEdit ?? defaultPresetId;
                            const restoredDefaultPreset =
                              rolledBack.find(
                                (p) =>
                                  String(p.id) === String(fallbackDefaultId),
                              ) || rolledBack[0];
                            if (restoredDefaultPreset) {
                              saveDefaultPresetToStorage(restoredDefaultPreset);
                            }
                            setEditingPreset(null);
                            setPendingDefaultPresetId(null);
                            setDefaultPresetIdBeforeEdit(null);
                            setPendingNewPresetId(null);
                          }}
                          style={{
                            backgroundColor: "#e5e7eb",
                            color: "#111827",
                            padding: "6px 12px",
                            borderRadius: "4px",
                            border: "none",
                            cursor: "pointer",
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
          {/* --- END NEW --- */}

          {/* Action Buttons */}
          <div
            style={{
              marginTop: "1.5rem",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            <button
              onClick={() => {
                if (selectedCircle.text) {
                  const willOpen = !showTextEditor;
                  // Set changingText flag BEFORE toggling editor to prevent any intermediate saves
                  if (willOpen) {
                    // Opening text editor - set changingText: true FIRST to prevent history saves
                    updateCircleProperties({ changingText: true });
                    // Use setTimeout to ensure the flag is set before any other updates
                    setTimeout(() => {
                      setShowTextEditor(true);
                    }, 0);
                  } else {
                    // Closing text editor - close first, then set changingText: false
                    setShowTextEditor(false);
                    // Set changingText: false to allow history save if there were changes
                    setTimeout(() => {
                      updateCircleProperties({ changingText: false });
                    }, 0);
                  }
                } else {
                  // Adding text for the first time - onAddText will save to history (expected)
                  onAddText(selectedCircle.id);
                  setShowTextEditor(true);
                  // Set changingText: true after adding text to prevent additional saves
                  setTimeout(() => {
                    updateCircleProperties({ changingText: true });
                  }, 100); // Delay to ensure onAddText's save completes first
                }
              }}
              style={{
                width: "100%",
                height: "40px",
                borderRadius: "5px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                border: "2px solid gray",
                backgroundColor: selectedCircle.text ? "white" : "#6c757e",
                color: selectedCircle.text ? "#000" : "#fff",
              }}
            >
              {selectedCircle.text ? "Edit Text in Shape" : "Add Text to Shape"}
            </button>

            <button
              onClick={onDelete}
              style={{
                width: "100%",
                height: "40px",
                backgroundColor: "white",
                borderRadius: "5px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                border: "2px solid red",
                color: "red",
                marginTop: "10px",
              }}
            >
              Delete Circle
            </button>
          </div>
        </>
      )}

      {showTextEditor && (
        <>
          <button
            onClick={() => {
              setShowTextEditor(false);
              // Set changingText: false when closing text editor to allow history save if there were changes
              updateCircleProperties({ changingText: false });
            }}
            style={{
              background: "transparent",
              border: "none",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              padding: "4px 8px",
              marginTop: "8px",
            }}
          >
            <svg
              width="17"
              height="16"
              viewBox="0 0 17 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M17 7.99998C17 7.71822 16.8881 7.44799 16.6888 7.24876C16.4896 7.04952 16.2194 6.9376 15.9376 6.9376H3.62887L8.19073 2.37786C8.28951 2.27908 8.36786 2.16182 8.42132 2.03276C8.47478 1.9037 8.50229 1.76538 8.50229 1.62569C8.50229 1.486 8.47478 1.34768 8.42132 1.21862C8.36786 1.08956 8.28951 0.972299 8.19073 0.873523C8.09196 0.774748 7.9747 0.696394 7.84564 0.642937C7.71658 0.589479 7.57826 0.561967 7.43857 0.561967C7.29888 0.561967 7.16056 0.589479 7.0315 0.642937C6.90244 0.696394 6.78518 0.774748 6.6864 0.873523L0.312114 7.24781C0.213179 7.3465 0.134683 7.46373 0.0811257 7.5928C0.027568 7.72187 0 7.86024 0 7.99998C0 8.13972 0.027568 8.27808 0.0811257 8.40715C0.134683 8.53622 0.213179 8.65346 0.312114 8.75214L6.6864 15.1264C6.88589 15.3259 7.15645 15.438 7.43857 15.438C7.72069 15.438 7.99125 15.3259 8.19073 15.1264C8.39022 14.9269 8.50229 14.6564 8.50229 14.3743C8.50229 14.0921 8.39022 13.8216 8.19073 13.6221L3.62887 9.06236H15.9376C16.2194 9.06236 16.4896 8.95043 16.6888 8.75119C16.8881 8.55196 17 8.28174 17 7.99998Z"
                fill="#6C757D"
              />
            </svg>

            <span>Edit Text in Shape</span>
          </button>

          {/* Text Alignment Controls */}
          <div
            style={{
              marginTop: "16px",
              display: "flex",
              gap: "12px",
              width: "100%",
            }}
          >
            {/* Horizontal Alignment Container */}
            <div
              style={{
                backgroundColor: "#f8f9fa",
                borderRadius: "6px",
                padding: "4px",
                display: "flex",
                gap: "2px",
                border: "1px solid #e9ecef",
                flex: 1,
              }}
            >
              {/* Left Align */}
              <button
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent button from stealing focus
                  // Save selection before clicking
                  if (window.saveLexicalSelection) {
                    window.saveLexicalSelection();
                  }
                }}
                onClick={() => {
                  // Alignment is a CSS property on the container, not the text content
                  // We should NOT update the text property to avoid re-initializing the editor
                  // This preserves all formatting including highlights
                  updateCircleProperties({ align: "left" });

                  // Since we're not updating text, the editor won't re-initialize
                  // So highlights should be preserved automatically
                  // Just restore focus and selection if it was lost
                  setTimeout(() => {
                    const editorElement = document.querySelector(
                      '[contenteditable="true"]',
                    );
                    if (editorElement) {
                      editorElement.focus();
                      // CRITICAL: Always restore selection and highlights after alignment change
                      // Button clicks always cause selection loss, so we need to restore
                      if (window.restoreLexicalSelection) {
                        window.restoreLexicalSelection();
                      }
                    }
                  }, 0);
                }}
                style={{
                  width: "32px",
                  height: "32px",
                  backgroundColor: align === "left" ? "#e4e6e7" : "transparent",
                  border: "none",
                  borderRadius: "4px",
                  display: "flex",
                  marginLeft: "5px",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: align === "left" ? "white" : "#495057",
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0 20V0H2V20H0ZM4 15V12H14V15H4ZM4 8V5H20V8H4Z"
                    fill="#6C757D"
                  />
                </svg>
              </button>

              {/* Center Align */}
              <button
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent button from stealing focus
                  // Save selection before clicking
                  if (window.saveLexicalSelection) {
                    window.saveLexicalSelection();
                  }
                }}
                onClick={() => {
                  // Alignment is a CSS property on the container, not the text content
                  // We should NOT update the text property to avoid re-initializing the editor
                  // This preserves all formatting including highlights
                  updateCircleProperties({ align: "center" });

                  // Since we're not updating text, the editor won't re-initialize
                  // So highlights should be preserved automatically
                  // Just restore focus and selection if it was lost
                  setTimeout(() => {
                    const editorElement = document.querySelector(
                      '[contenteditable="true"]',
                    );
                    if (editorElement) {
                      editorElement.focus();
                      // CRITICAL: Always restore selection and highlights after alignment change
                      // Button clicks always cause selection loss, so we need to restore
                      if (window.restoreLexicalSelection) {
                        window.restoreLexicalSelection();
                      } else {
                      }
                    }
                  }, 0);
                }}
                style={{
                  width: "32px",
                  height: "32px",
                  backgroundColor:
                    align === "center" ? "#e4e6e7" : "transparent",
                  border: "none",
                  borderRadius: "4px",
                  display: "flex",
                  marginLeft: "5px",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: align === "center" ? "white" : "#495057",
                }}
              >
                <svg
                  width="18"
                  height="20"
                  viewBox="0 0 18 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8 20V15H3V12H8V8H0V5H8V0H10V5H18V8H10V12H15V15H10V20H8Z"
                    fill="#6C757D"
                  />
                </svg>
              </button>

              {/* Right Align */}
              <button
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent button from stealing focus
                  // Save selection before clicking
                  if (window.saveLexicalSelection) {
                    window.saveLexicalSelection();
                  }
                }}
                onClick={() => {
                  // Alignment is a CSS property on the container, not the text content
                  // We should NOT update the text property to avoid re-initializing the editor
                  // This preserves all formatting including highlights
                  updateCircleProperties({ align: "right" });

                  // Since we're not updating text, the editor won't re-initialize
                  // So highlights should be preserved automatically
                  // Just restore focus and selection if it was lost
                  setTimeout(() => {
                    const editorElement = document.querySelector(
                      '[contenteditable="true"]',
                    );
                    if (editorElement) {
                      editorElement.focus();
                      // CRITICAL: Always restore selection and highlights after alignment change
                      // Button clicks always cause selection loss, so we need to restore
                      if (window.restoreLexicalSelection) {
                        window.restoreLexicalSelection();
                      } else {
                      }
                    }
                  }, 0);
                }}
                style={{
                  width: "32px",
                  height: "32px",
                  backgroundColor:
                    align === "right" ? "#e4e6e7" : "transparent",
                  border: "none",
                  borderRadius: "4px",
                  display: "flex",
                  marginLeft: "5px",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: align === "right" ? "white" : "#495057",
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 20V0H20V20H18ZM6 15V12H16V15H6ZM0 8V5H16V8H0Z"
                    fill="#6C757D"
                  />
                </svg>
              </button>
            </div>

            {/* Vertical Alignment Container */}
            <div
              style={{
                backgroundColor: "#f8f9fa",
                borderRadius: "6px",
                padding: "4px",
                display: "flex",
                gap: "2px",
                border: "1px solid #e9ecef",
                flex: 1,
              }}
            >
              {/* Top Align */}
              <button
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent button from stealing focus
                  // Save selection before clicking
                  if (window.saveLexicalSelection) {
                    window.saveLexicalSelection();
                  }
                }}
                onClick={() => {
                  // Alignment is a CSS property on the container, not the text content
                  // We should NOT update the text property to avoid re-initializing the editor
                  // This preserves all formatting including highlights
                  updateCircleProperties({ verticalAlign: "top" });

                  // Since we're not updating text, the editor won't re-initialize
                  // So highlights should be preserved automatically
                  // Just restore focus and selection if it was lost
                  setTimeout(() => {
                    const editorElement = document.querySelector(
                      '[contenteditable="true"]',
                    );
                    if (editorElement) {
                      editorElement.focus();
                      // CRITICAL: Always restore selection and highlights after alignment change
                      // Button clicks always cause selection loss, so we need to restore
                      if (window.restoreLexicalSelection) {
                        window.restoreLexicalSelection();
                      } else {
                      }
                    }
                  }, 0);
                }}
                style={{
                  width: "32px",
                  height: "32px",
                  backgroundColor:
                    verticalAlign === "top" ? "#e4e6e7" : "transparent",
                  border: "none",
                  borderRadius: "4px",
                  display: "flex",
                  marginLeft: "5px",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: verticalAlign === "top" ? "white" : "#495057",
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5 20V4H8V20H5ZM12 14V4H15V14H12ZM0 2V0H20V2H0Z"
                    fill="#6C757D"
                  />
                </svg>
              </button>

              {/* Middle Align */}
              <button
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent button from stealing focus
                  // Save selection before clicking
                  if (window.saveLexicalSelection) {
                    window.saveLexicalSelection();
                  }
                }}
                onClick={() => {
                  // Alignment is a CSS property on the container, not the text content
                  // We should NOT update the text property to avoid re-initializing the editor
                  // This preserves all formatting including highlights
                  updateCircleProperties({ verticalAlign: "middle" });

                  // Since we're not updating text, the editor won't re-initialize
                  // So highlights should be preserved automatically
                  // Just restore focus and selection if it was lost
                  setTimeout(() => {
                    const editorElement = document.querySelector(
                      '[contenteditable="true"]',
                    );
                    if (editorElement) {
                      editorElement.focus();
                      // CRITICAL: Always restore selection and highlights after alignment change
                      // Button clicks always cause selection loss, so we need to restore
                      if (window.restoreLexicalSelection) {
                        window.restoreLexicalSelection();
                      } else {
                      }
                    }
                  }, 0);
                }}
                style={{
                  width: "32px",
                  height: "32px",
                  backgroundColor:
                    verticalAlign === "middle" ? "#e4e6e7" : "transparent",
                  border: "none",
                  borderRadius: "4px",
                  display: "flex",
                  marginLeft: "5px",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: verticalAlign === "middle" ? "white" : "#495057",
                }}
              >
                <svg
                  width="20"
                  height="18"
                  viewBox="0 0 20 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5 18V10H0V8H5V0H8V8H12V3H15V8H20V10H15V15H12V10H8V18H5Z"
                    fill="#6C757D"
                  />
                </svg>
              </button>

              {/* Bottom Align */}
              <button
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent button from stealing focus
                  // Save selection before clicking
                  if (window.saveLexicalSelection) {
                    window.saveLexicalSelection();
                  }
                }}
                onClick={() => {
                  // Alignment is a CSS property on the container, not the text content
                  // We should NOT update the text property to avoid re-initializing the editor
                  // This preserves all formatting including highlights
                  updateCircleProperties({ verticalAlign: "bottom" });

                  // Since we're not updating text, the editor won't re-initialize
                  // So highlights should be preserved automatically
                  // Just restore focus and selection if it was lost
                  setTimeout(() => {
                    const editorElement = document.querySelector(
                      '[contenteditable="true"]',
                    );
                    if (editorElement) {
                      editorElement.focus();
                      // CRITICAL: Always restore selection and highlights after alignment change
                      // Button clicks always cause selection loss, so we need to restore
                      if (window.restoreLexicalSelection) {
                        window.restoreLexicalSelection();
                      } else {
                      }
                    }
                  }, 0);
                }}
                style={{
                  width: "32px",
                  height: "32px",
                  backgroundColor:
                    verticalAlign === "bottom" ? "#e4e6e7" : "transparent",
                  border: "none",
                  borderRadius: "4px",
                  marginLeft: "5px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: verticalAlign === "bottom" ? "white" : "#495057",
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0 20V18H20V20H0ZM5 16V0H8V16H5ZM12 16V6H15V16H12Z"
                    fill="#6C757D"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Text Styling Controls */}
          <div
            style={{
              marginTop: "12px",
              display: "flex",
              gap: "8px",
              width: "100%",
            }}
          >
            {/* Highlight Button */}
            <button
              onClick={toggleHighlight}
              title={isHighlighted ? "Remove Highlight" : "Add Highlight"}
              style={{
                flex: 1,
                height: "40px",
                backgroundColor: isHighlighted ? "#e1e6ea" : "#f8f9fa",
                borderRadius: "4px",
                border: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: isHighlighted ? "white" : "#6C757D",
                transition: "all 0.2s ease",
              }}
            >
              <svg
                width="100%"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.1875 16V13.7554H15.1516V16H0.1875ZM9.39192 7.12946L7.44659 5.18413L4.49679 8.13374C4.45364 8.17689 4.43207 8.22964 4.43207 8.29199C4.43207 8.35434 4.45364 8.40715 4.49679 8.45042L6.11404 10.0791C6.15731 10.1222 6.21012 10.1438 6.27247 10.1438C6.33482 10.1438 6.38757 10.1222 6.43071 10.0791L9.39192 7.12946ZM8.24642 4.39571L10.1803 6.32944L13.3746 3.14676C13.4178 3.10362 13.4393 3.04844 13.4393 2.98123C13.4393 2.91401 13.4178 2.85883 13.3746 2.81568L11.7602 1.20143C11.717 1.15828 11.6619 1.13671 11.5948 1.13671C11.5276 1.13671 11.4724 1.15828 11.4293 1.20143L8.24642 4.39571ZM7.06369 3.98999L10.5861 7.51217L7.23073 10.8791C6.96013 11.1496 6.64258 11.2848 6.27808 11.2848C5.91358 11.2848 5.59609 11.1496 5.32561 10.8791L5.2536 10.8071L4.5688 11.446H1.6624L3.78899 9.33087L3.73137 9.27345C3.4609 9.00284 3.3218 8.68149 3.31406 8.30938C3.30646 7.93715 3.43789 7.6158 3.70837 7.34532L7.06369 3.98999ZM7.06369 3.98999L10.648 0.405715C10.9185 0.135238 11.2359 0 11.6004 0C11.9649 0 12.2825 0.135238 12.5531 0.405715L14.1817 2.02296C14.4522 2.29344 14.5875 2.61093 14.5875 2.97543C14.5875 3.33993 14.4522 3.65742 14.1817 3.92789L10.5861 7.51217L7.06369 3.98999Z"
                  fill={isHighlighted ? "#6C757D" : "#6C757D"}
                />
              </svg>
            </button>
            <button
              onClick={toggleBold}
              style={{
                flex: 1,
                height: "40px",
                backgroundColor: isBold ? "#e1e6ea" : "#f8f9fa",
                border: "1px solid #ccc",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                fontWeight: "bold",
                fontSize: "14px",
              }}
            >
              <svg
                width="100%"
                height="14"
                viewBox="0 0 12 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.804688 14V0H6.32969C7.41302 0 8.41302 0.333333 9.32969 1C10.2464 1.66667 10.7047 2.59167 10.7047 3.775C10.7047 4.625 10.513 5.27917 10.1297 5.7375C9.74636 6.19583 9.38802 6.525 9.05469 6.725C9.47135 6.90833 9.93386 7.25 10.4422 7.75C10.9505 8.25 11.2047 9 11.2047 10C11.2047 11.4833 10.663 12.5208 9.57969 13.1125C8.49636 13.7042 7.47969 14 6.52969 14H0.804688ZM3.82969 11.2H6.42969C7.22969 11.2 7.71719 10.9958 7.89219 10.5875C8.06719 10.1792 8.15469 9.88333 8.15469 9.7C8.15469 9.51667 8.06719 9.22083 7.89219 8.8125C7.71719 8.40417 7.20469 8.2 6.35469 8.2H3.82969V11.2ZM3.82969 5.5H6.15469C6.70469 5.5 7.10469 5.35833 7.35469 5.075C7.60469 4.79167 7.72969 4.475 7.72969 4.125C7.72969 3.725 7.58802 3.4 7.30469 3.15C7.02135 2.9 6.65469 2.775 6.20469 2.775H3.82969V5.5Z"
                  fill={isBold ? "#6C757D" : "#6C757D"}
                />
              </svg>
            </button>
            <button
              onClick={toggleItalic}
              style={{
                flex: 1,
                height: "40px",
                backgroundColor: isItalic ? "#e1e6ea" : "#f8f9fa",
                border: "1px solid #ccc",
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                fontStyle: "italic",
                fontSize: "14px",
              }}
            >
              <svg
                width="100%"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.828125 14V11.5H4.82812L7.82812 2.5H3.82812V0H13.8281V2.5H10.3281L7.32812 11.5H10.8281V14H0.828125Z"
                  fill={isItalic ? "#6C757D" : "#6C757D"}
                />
              </svg>
            </button>
          </div>

          {/* Color Selection */}
          <div
            style={{
              marginTop: "12px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            {/* Color Swatches */}
            <div style={{ display: "flex", gap: "4px" }}>
              <button
                onMouseDown={(e) => {
                  // Save selection before clicking
                  if (window.saveLexicalSelection) {
                    window.saveLexicalSelection();
                  }
                }}
                onClick={() => applyColorToEntireText("#007bff")}
                style={{
                  width: "24px",
                  height: "24px",
                  backgroundColor: "#007bff",
                  border: "1px solid #ccc",
                  borderRadius: "2px",
                  cursor: "pointer",
                }}
              />
              <button
                onMouseDown={(e) => {
                  // Save selection before clicking
                  if (window.saveLexicalSelection) {
                    window.saveLexicalSelection();
                  }
                }}
                onClick={() => applyColorToEntireText("#FF0000")}
                style={{
                  width: "24px",
                  height: "24px",
                  backgroundColor: "#FF0000",
                  border: "1px solid #ccc",
                  borderRadius: "2px",
                  cursor: "pointer",
                }}
              />
              <button
                onMouseDown={(e) => {
                  // Save selection before clicking
                  if (window.saveLexicalSelection) {
                    window.saveLexicalSelection();
                  }
                }}
                onClick={() => applyColorToEntireText("#28a745")}
                style={{
                  width: "24px",
                  height: "24px",
                  backgroundColor: "#28a745",
                  border: "1px solid #ccc",
                  borderRadius: "2px",
                  cursor: "pointer",
                }}
              />
              <button
                onMouseDown={(e) => {
                  // Save selection before clicking
                  if (window.saveLexicalSelection) {
                    window.saveLexicalSelection();
                  }
                }}
                onClick={() => applyColorToEntireText("#212529")}
                style={{
                  width: "24px",
                  height: "24px",
                  backgroundColor: "#212529",
                  border: "1px solid #ccc",
                  borderRadius: "2px",
                  cursor: "pointer",
                }}
              />
            </div>

            {/* Color Picker */}
            <input
              type="color"
              value={textColor}
              onChange={handleTextColorChange}
              onFocus={() => {
                // Save the current Lexical selection before focusing color picker
                if (window.saveLexicalSelection) {
                  window.saveLexicalSelection();
                }
              }}
              style={{
                width: "40px",
                height: "24px",
                border: "1px solid #ccc",
                borderRadius: "2px",
                cursor: "pointer",
                padding: 0,
              }}
            />

            {/* Hex Input */}
            <input
              type="text"
              value={`Hex ${localInputValues.textColorHex}`}
              maxLength={10}
              onKeyDown={handleEnterKey}
              onChange={(e) => {
                // Strip "Hex " prefix if present, then process hex value
                let inputValue = e.target.value
                  .replace(/^Hex\s*/i, "")
                  .toUpperCase()
                  .replace("#", "");
                // Limit to max 6 hex characters
                if (inputValue.length > 6) {
                  inputValue = inputValue.slice(0, 6);
                }
                setLocalInputValues((prev) => ({
                  ...prev,
                  textColorHex: inputValue,
                }));
              }}
              onBlur={(e) => {
                // Strip "Hex " prefix before validating
                const hexValue = e.target.value.replace(/^Hex\s*/i, "");
                const validColor = validateAndParseHexColor(hexValue);

                if (validColor) {
                  // Valid hex code - update the text color
                  handleTextColorChange({ target: { value: validColor } });
                } else {
                  // Invalid hex code - revert to current color
                  const currentHex = textColor.toUpperCase().replace("#", "");
                  setLocalInputValues((prev) => ({
                    ...prev,
                    textColorHex: currentHex,
                  }));
                }
              }}
              onFocus={() => {
                // Clear any previous highlights first
                if (window.clearLexicalSelectionHighlight) {
                  window.clearLexicalSelectionHighlight();
                }

                // Save the current Lexical selection before focusing input
                if (window.saveLexicalSelection) {
                  window.saveLexicalSelection();
                }
              }}
              data-color-input="true"
              placeholder="Hex 000000"
              style={{
                flex: 1,
                padding: "6px 8px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                backgroundColor: "white",
                fontSize: "12px",
              }}
            />
          </div>

          {/* Font Size */}
          <div style={{ marginTop: "16px" }}>
            <label
              style={{
                display: "block",
                fontSize: "13px",
                marginBottom: "4px",
              }}
            >
              Font Size:
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <button
                onMouseDown={(e) => {
                  e.preventDefault(); // Only prevent on buttons to avoid stealing focus
                  handleFontSizeDecrement(e);
                }}
                style={{
                  width: "32px",
                  height: "32px",
                  border: "1px solid #ccc",
                  borderTopLeftRadius: "4px",
                  borderBottomLeftRadius: "4px",
                  backgroundColor: "#f8f9fa",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                  color: "#6C757D",
                }}
                title="Decrease font size"
              >
                -
              </button>

              <input
                key={`font-size-${formattingUpdate}`}
                type="number"
                value={fontSizeInputValue}
                min={dynamicMinFontSize}
                max={MAX_FONT_SIZE}
                onChange={(e) => {
                  const newValue = parseInt(e.target.value, 10);
                  setFontSizeInputValue(
                    Number.isFinite(newValue) ? newValue : "",
                  );
                  // Don't apply changes on every keystroke to preserve selection
                }}
                onKeyDown={(e) => {
                  // Handle arrow keys to apply font size changes immediately
                  if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                    // Prevent default to avoid the input from taking focus
                    e.preventDefault();

                    // Set flag FIRST to prevent any interference
                    isProgrammaticallyChangingFontSizeRef.current = true;

                    // Calculate new font size - use the input value directly, not getCurrentFontSize()
                    // to avoid reading stale values from window.lexicalSelectedFontInfo
                    const currentValue = parseInt(e.target.value, 10);
                    if (!Number.isFinite(currentValue)) {
                      // If input is empty/invalid, use fontSize prop as fallback
                      const fallbackValue = fontSize || 14;
                      const newFontSize =
                        e.key === "ArrowUp"
                          ? fallbackValue + 1
                          : fallbackValue - 1;
                      setFontSizeInputValue(newFontSize);
                      handleFontSizeChange(
                        {
                          target: { value: newFontSize.toString() },
                        },
                        true,
                      );
                      return;
                    }
                    const newFontSize =
                      e.key === "ArrowUp" ? currentValue + 1 : currentValue - 1;

                    // Update the input value
                    setFontSizeInputValue(newFontSize);

                    // Apply the font size change immediately, skipping DOM verification
                    // to prevent reading stale values that cause flickering
                    handleFontSizeChange(
                      {
                        target: { value: newFontSize.toString() },
                      },
                      true,
                    ); // true = skip verification
                  } else if (e.key === "Enter") {
                    // Apply on Enter key - selection restoration handled in handleFontSizeChange
                    e.preventDefault();
                    if (
                      e.target.value !== "" &&
                      !isNaN(parseInt(e.target.value, 10))
                    ) {
                      handleFontSizeChange(e);
                    }
                  }
                }}
                onBlur={(e) => {
                  // Apply font size change when user finishes editing
                  // Selection restoration is now handled inside handleFontSizeChange
                  if (
                    e.target.value !== "" &&
                    !isNaN(parseInt(e.target.value, 10))
                  ) {
                    handleFontSizeChange(e);
                  }
                }}
                onFocus={() => {
                  // Clear any previous highlights first
                  if (window.clearLexicalSelectionHighlight) {
                    window.clearLexicalSelectionHighlight();
                  }

                  setFontSizeInputValue(getCurrentFontSize());

                  // Save the current Lexical selection before focusing input
                  if (window.saveLexicalSelection) {
                    window.saveLexicalSelection();
                  }
                }}
                data-font-size-input="true"
                className="font-size-input"
                style={{
                  border: "1px solid #ccc",
                  borderLeft: "none",
                  borderRight: "none",
                }}
              />

              <button
                onMouseDown={(e) => {
                  e.preventDefault(); // Only prevent on buttons to avoid stealing focus
                  handleFontSizeIncrement(e);
                }}
                style={{
                  width: "32px",
                  height: "32px",
                  border: "1px solid #ccc",
                  borderTopRightRadius: "4px",
                  borderBottomRightRadius: "4px",
                  backgroundColor: "#f8f9fa",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                  color: "#6C757D",
                }}
                title="Increase font size"
              >
                +
              </button>
            </div>
          </div>

          {/* Text Input Area */}
          <div style={{ marginTop: "16px" }}>
            <SimpleLexicalEditorWrapper
              value={textContent}
              onChange={(content) => {
                updateCircleProperties({
                  text: content,
                  changingText: true,
                });
              }}
              onBlur={() => {
                updateCircleProperties({
                  changingText: false,
                });
              }}
              placeholder="Enter text..."
              autoFocus={true}
              style={{
                height: "100px",
                fontFamily: "Arial, sans-serif",
                fontSize: `${fontSize}px`,
                fontWeight: fontStyle.includes("bold") ? "bold" : "normal",
                fontStyle: fontStyle.includes("italic") ? "italic" : "normal",
                textDecoration: textDecoration,
                color: textColor,
                textAlign: align,
                lineHeight: "1.2",
              }}
            />
          </div>

          {/* Remove Text Button */}
          <div style={{ marginTop: "20px" }}>
            <button
              onClick={() => {
                onRemoveText(selectedCircle.id);
                setShowTextEditor(false);
              }}
              style={{
                width: "100%",
                height: "40px",
                backgroundColor: "white",
                borderRadius: "5px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                border: "2px solid red",
                color: "red",
                marginTop: "10px",
              }}
            >
              Remove Text
            </button>
          </div>
        </>
      )}
    </div>
  );
}

CircleEditor.propTypes = {
  selectedCircle: PropTypes.object.isRequired,
  updateCircleProperties: PropTypes.func.isRequired,
  onAddText: PropTypes.func.isRequired,
  onEditText: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onRemoveText: PropTypes.func.isRequired,
};

export default CircleEditor;
